import { useState, useEffect, useRef } from "react";
import { supabase } from './supabase';
import AuthForm from './AuthForm';
import LandingFlow from './components/LandingFlow';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import CallHistory from './components/CallHistory';
import CallScreen from './components/CallScreen';
import OPIWidget from './components/OPIWidget';
import InterpreterModal from './components/InterpreterModal';
import { io } from "socket.io-client";
import { GLOBAL_TEMPLATES } from "./templates";
import {
  listUserTemplates,
  saveGlobalOverride,
  createUserTemplate,
  updateUserTemplate,
  deleteUserTemplate,
  mergeTemplates,
} from "./templatesApi";

const opusAnimCSS = [
  "@keyframes blink{0%,49%{opacity:1}50%,100%{opacity:0}}",
  "@keyframes pulse{0%,100%{transform:scale(1);opacity:.12}50%{transform:scale(1.4);opacity:.04}}",
  "@keyframes coreBreath{0%,100%{opacity:.5}50%{opacity:.25}}",
  "@keyframes moveRight{0%{transform:translateX(0)}100%{transform:translateX(175px)}}",
  "@keyframes moveLeft{0%{transform:translateX(0)}100%{transform:translateX(-175px)}}",
  "@keyframes showFirst{0%,5%{opacity:0}10%,42%{opacity:.85}50%,100%{opacity:0}}",
  "@keyframes showSecond{0%,48%{opacity:0}55%,88%{opacity:.65}95%,100%{opacity:0}}",
  "@keyframes showFirstR{0%,5%{opacity:0}10%,42%{opacity:.6}50%,100%{opacity:0}}",
  "@keyframes showSecondR{0%,48%{opacity:0}55%,88%{opacity:.85}95%,100%{opacity:0}}",
  "@keyframes newLine{0%,80%{opacity:0;transform:translateY(3px)}85%,95%{opacity:.4;transform:translateY(0)}100%{opacity:0}}",
  "@keyframes ringOut{0%,100%{r:3;opacity:.15}50%{r:7;opacity:0}}"
].join(" ");


// CRA uses process.env.REACT_APP_* for env vars
const API_PRIMARY = "https://api.myrelay.ai";
const API_FALLBACK = "https://voicebridge-backend-production-e379.up.railway.app";
let API_URL = process.env.REACT_APP_API_URL || API_PRIMARY;

// Resilient fetch — adds auth token, tries primary, falls back to Railway
const apiFetch = async (path, opts = {}) => {
  // Get Supabase JWT and inject into headers
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) {
      opts.headers = {
        ...opts.headers,
        Authorization: `Bearer ${session.access_token}`,
      };
    }
  } catch (e) {
    // Continue without auth token (health check etc.)
  }

  try {
    const res = await fetch(`${API_URL}${path}`, opts);
    return res;
  } catch (e) {
    if (API_URL === API_PRIMARY) {
      console.warn(`[API] Primary unreachable, falling back to Railway`);
      API_URL = API_FALLBACK;
      return fetch(`${API_URL}${path}`, opts);
    }
    throw e;
  }
};

// 5 real voices + 1 "Coming soon" placeholder. Each real voice maps to a
// DISTINCT Grok voice in backend/src/services/grok.js (rex/leo/eve/ara/sal).
// If you add a 6th real voice, Grok only has 5 — something would have to collide.
const VOICES = [
  { id: "pNInz6obpgDQGcFmaJgB", name: "Adam",   tag: "Deep · Pro",       gender: "M", gradient: "linear-gradient(135deg,#6366F1,#4338CA)" },
  { id: "TxGEqnHWrfWFTfGW9XjX", name: "Josh",   tag: "Warm · Friendly",  gender: "M", gradient: "linear-gradient(135deg,#3B82F6,#1D4ED8)" },
  { id: "EXAVITQu4vr4xnSDxMaL", name: "Sarah",  tag: "Soft · Natural",   gender: "F", gradient: "linear-gradient(135deg,#EC4899,#BE185D)" },
  { id: "21m00Tcm4tlvDq8ikWAM", name: "Rachel", tag: "Bright · Upbeat",  gender: "F", gradient: "linear-gradient(135deg,#F59E0B,#D97706)" },
  { id: "2EiwWnXFnvU5JabPnv8n", name: "Marcus", tag: "Smooth · Bold",    gender: "M", gradient: "linear-gradient(135deg,#10B981,#059669)" },
  { id: "__coming_soon__",      name: "Coming soon", tag: "New voice",   gender: "",  gradient: "linear-gradient(135deg,#6B7280,#4B5563)", comingSoon: true },
];

const LANGUAGES = [
  { code: "es", name: "Spanish",    flag: "🇪🇸" },
  { code: "fr", name: "French",     flag: "🇫🇷" },
  { code: "de", name: "German",     flag: "🇩🇪" },
  { code: "pt-BR", name: "Portuguese", flag: "🇧🇷" },
  { code: "it", name: "Italian",    flag: "🇮🇹" },
  { code: "zh", name: "Chinese",    flag: "🇨🇳" },
  { code: "ja", name: "Japanese",   flag: "🇯🇵" },
  { code: "ko", name: "Korean",     flag: "🇰🇷" },
  { code: "ar", name: "Arabic",     flag: "🇸🇦" },
  { code: "hi", name: "Hindi",      flag: "🇮🇳" },
  { code: "ru", name: "Russian",    flag: "🇷🇺" },
  { code: "nl", name: "Dutch",      flag: "🇳🇱" },
  { code: "pl", name: "Polish",     flag: "🇵🇱" },
  { code: "tr", name: "Turkish",    flag: "🇹🇷" },
  { code: "sv", name: "Swedish",    flag: "🇸🇪" },
  { code: "da", name: "Danish",     flag: "🇩🇰" },
  { code: "id", name: "Indonesian", flag: "🇮🇩" },
  { code: "uk", name: "Ukrainian",  flag: "🇺🇦" },
  { code: "vi", name: "Vietnamese", flag: "🇻🇳" },
  { code: "tl", name: "Filipino",   flag: "🇵🇭" },
];

const KEYS = ["1","2","3","4","5","6","7","8","9","*","0","#"];
const KEY_LETTERS = {"2":"ABC","3":"DEF","4":"GHI","5":"JKL","6":"MNO","7":"PQRS","8":"TUV","9":"WXYZ"};

// ── Inline SVG icons ──────────────────────
const Icon = ({d, size=16, color="currentColor", sw=2}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
    {typeof d === "string" ? <path d={d}/> : d}
  </svg>
);
const IconSend = ({size=16,color="currentColor"}) => <Icon size={size} color={color} d={<><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2" fill="none"/></>}/>;
const IconDelete = ({size=16,color="currentColor"}) => <Icon size={size} color={color} d={<><path d="M21 4H8l-7 8 7 8h13a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z"/><line x1="18" y1="9" x2="12" y2="15"/><line x1="12" y1="9" x2="18" y2="15"/></>}/>;
const IconClose = ({size=14,color="currentColor"}) => <Icon size={size} color={color} sw={2.5} d={<><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>}/>;
const IconSun = ({size=14,color="currentColor"}) => <Icon size={size} color={color} d={<><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></>}/>;
const IconMoon = ({size=14,color="currentColor"}) => <Icon size={size} color={color} d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>;
const IconPen = ({size=14,color="currentColor"}) => <Icon size={size} color={color} d={<><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></>}/>;
const IconBot = ({size=14,color="currentColor"}) => <Icon size={size} color={color} d={<><rect x="3" y="11" width="18" height="10" rx="2"/><circle cx="12" cy="5" r="2"/><path d="M12 7v4"/><line x1="8" y1="16" x2="8" y2="16"/><line x1="16" y1="16" x2="16" y2="16"/></>}/>;
const IconPhone = ({size=16,color="currentColor"}) => <Icon size={size} color={color} d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.362 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0 1 22 16.92Z"/>;
const IconMic = ({size=16,color="currentColor"}) => <Icon size={size} color={color} d={<><rect x="9" y="2" width="6" height="11" rx="3"/><path d="M5 10a7 7 0 0 0 14 0"/><line x1="12" y1="19" x2="12" y2="22"/><line x1="8" y1="22" x2="16" y2="22"/></>}/>;
const IconShield = ({size=16,color="currentColor"}) => <Icon size={size} color={color} d={<><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></>}/>;
const IconUser = ({size=16,color="currentColor"}) => <Icon size={size} color={color} d={<><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></>}/>;
const IconSettings = ({size=16,color="currentColor"}) => <Icon size={size} color={color} d={<><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></>}/>;
const IconPlus = ({size=16,color="currentColor"}) => <Icon size={size} color={color} d={<><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></>}/>;
const IconTrash = ({size=14,color="currentColor"}) => <Icon size={size} color={color} d={<><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></>}/>;

// ── Avatar components ──────────────────────
function AvatarRobot({ state, size=64 }) {
  const listening = state==="listening", speaking=state==="speaking";
  const c = listening?"#818cf8":speaking?"#10b981":"#3f3f46";
  const glow = listening?"drop-shadow(0 0 8px rgba(129,140,248,0.5))":speaking?"drop-shadow(0 0 8px rgba(16,185,129,0.4))":"none";
  return (
    <svg width={size} height={size*1.1} viewBox="0 0 64 72" fill="none" style={{filter:glow,transition:"filter 0.4s"}}>
      <line x1="32" y1="2" x2="32" y2="10" stroke={c} strokeWidth="1.5" strokeLinecap="round"/>
      <circle cx="32" cy="2" r="2" fill={c}/>
      <rect x="14" y="10" width="36" height="26" rx="7" fill="#111113" stroke={c} strokeWidth="1.2"/>
      <rect x="19" y="17" width="9" height="7" rx="3" fill={c} opacity="0.9"/>
      <rect x="36" y="17" width="9" height="7" rx="3" fill={c} opacity="0.9"/>
      <rect x="21.5" y="19" width="3" height="2" rx="1" fill="white" opacity="0.5"/>
      <rect x="38.5" y="19" width="3" height="2" rx="1" fill="white" opacity="0.5"/>
      {speaking?[0,1,2,3].map(i=><rect key={i} x={20+i*7} y={30} width="5" height={2+i%2*3} rx="1.5" fill={c} opacity="0.8"/>)
        :listening?<path d="M21 31 Q32 36 43 31" stroke={c} strokeWidth="1.5" fill="none" strokeLinecap="round"/>
        :<line x1="23" y1="31.5" x2="41" y2="31.5" stroke={c} strokeWidth="1.2" strokeLinecap="round"/>}
      <rect x="28" y="36" width="8" height="4" rx="2" fill="#111113" stroke="#27272a" strokeWidth="1"/>
      <rect x="9" y="40" width="46" height="28" rx="6" fill="#111113" stroke={c} strokeWidth="1.2"/>
      <rect x="17" y="46" width="30" height="16" rx="3" fill="#09090b" stroke="#27272a" strokeWidth="1"/>
      {listening?[0,1,2,3,4].map(i=>{const h=3+Math.abs(2-i)*4;return<rect key={i} x={19+i*5} y={54-h/2} width="3.5" height={h} rx="1.5" fill={c} opacity="0.8"/>;})
        :<text x="32" y="57" textAnchor="middle" fill={c} fontSize="6" fontFamily="monospace" opacity="0.5">READY</text>}
      <rect x="1" y="42" width="7" height="18" rx="3.5" fill="#111113" stroke="#27272a" strokeWidth="1"/>
      <rect x="56" y="42" width="7" height="18" rx="3.5" fill="#111113" stroke="#27272a" strokeWidth="1"/>
    </svg>
  );
}

function AvatarOrb({ state, size=64 }) {
  const listening = state==="listening", speaking=state==="speaking";
  const c = listening?"#818cf8":speaking?"#10b981":"#3f3f46";
  const bg = listening?"rgba(129,140,248,0.12)":speaking?"rgba(16,185,129,0.12)":"rgba(63,63,70,0.08)";
  const glow = listening?"drop-shadow(0 0 10px rgba(129,140,248,0.5))":speaking?"drop-shadow(0 0 10px rgba(16,185,129,0.4))":"none";
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" style={{filter:glow,transition:"filter 0.4s"}}>
      <circle cx="32" cy="32" r="28" fill={bg} stroke={c} strokeWidth="1.2"/>
      <circle cx="32" cy="32" r="20" fill="none" stroke={c} strokeWidth="0.6" opacity="0.3"/>
      {listening
        ? [0,1,2,3,4,5,6].map(i=>{const h=4+Math.abs(3-i)*5;return<rect key={i} x={16+i*5} y={32-h/2} width="3.5" height={h} rx="1.8" fill={c} opacity="0.8"/>;})
        : speaking
        ? [0,1,2,3,4].map(i=><circle key={i} cx={20+i*6} cy={32} r={2+i%2*2} fill={c} opacity={0.4+i*0.1}/>)
        : <><circle cx="32" cy="32" r="6" fill={c} opacity="0.4"/><circle cx="32" cy="32" r="2.5" fill={c}/></>}
      <text x="32" y="54" textAnchor="middle" fill={c} fontSize="5.5" fontFamily="monospace" opacity="0.5" letterSpacing="1">
        {listening?"LISTENING":speaking?"SPEAKING":"STANDBY"}
      </text>
    </svg>
  );
}

function AvatarWave({ state, size=64 }) {
  const listening = state==="listening", speaking=state==="speaking";
  const c = listening?"#818cf8":speaking?"#10b981":"#3f3f46";
  const glow = listening?"drop-shadow(0 0 8px rgba(129,140,248,0.5))":speaking?"drop-shadow(0 0 8px rgba(16,185,129,0.4))":"none";
  const bars = listening?[8,14,20,28,20,14,8]:speaking?[6,10,18,24,18,10,6]:[4,4,4,4,4,4,4];
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" style={{filter:glow,transition:"filter 0.4s"}}>
      <rect x="2" y="2" width="60" height="60" rx="14" fill="#111113" stroke={c} strokeWidth="1.2"/>
      <text x="32" y="14" textAnchor="middle" fill={c} fontSize="6" fontFamily="monospace" opacity="0.5" letterSpacing="1">myrelay</text>
      {bars.map((h,i)=>(
        <rect key={i} x={10+i*7} y={32-h/2} width="5" height={h} rx="2.5" fill={c} opacity={0.5+i*0.05}/>
      ))}
      <text x="32" y="56" textAnchor="middle" fill={c} fontSize="5.5" fontFamily="monospace" opacity="0.5" letterSpacing="1">
        {listening?"● REC":speaking?"▶ OUT":"– IDLE"}
      </text>
    </svg>
  );
}

const AVATARS = [
  { id:"robot", label:"Robot",  Component: AvatarRobot },
  { id:"orb",   label:"Orb",    Component: AvatarOrb   },
  { id:"wave",  label:"Wave",   Component: AvatarWave  },
  { id:"none",  label:"None",   Component: null        },
];

function Avatar({ avatarId, state, size=64 }) {
  const av = AVATARS.find(a=>a.id===avatarId) || AVATARS[0];
  if(!av.Component) return null;
  const C = av.Component;
  return <C state={state} size={size}/>;
}

const DARK = {
  "--bg":"#0f1623","--s1":"#1a2236","--s2":"#1e2a40","--s3":"#2a3a55",
  "--b1":"#2a3a55","--b2":"#3a4f6a",
  "--tx":"#f0f4ff","--tx2":"#9aabc2","--tx3":"#5a7090",
  "--ac":"#4f7cff","--ac2":"#93b4ff","--ac3":"rgba(79,124,255,0.18)",
  "--ok":"#10b981","--err":"#f43f5e",
  "--sel-voice-bg":"transparent","--sel-voice-border":"#4f7cff","--sel-voice-tx":"#4f7cff","--sel-voice-tag":"#93b4ff",
  "--sel-av-bg":"rgba(79,124,255,0.15)","--sel-av-border":"#4f7cff","--sel-av-tx":"#93b4ff",
  "--card-hover":"rgba(79,124,255,0.04)",
};
const LIGHT = {
  "--bg":"#eef3ff","--s1":"#ffffff","--s2":"#dce8ff","--s3":"#c0d0ee",
  "--b1":"#c0d0ee","--b2":"#9aabc2",
  "--tx":"#0f1623","--tx2":"#2a3a55","--tx3":"#5a7090",
  "--ac":"#4f7cff","--ac2":"#2952cc","--ac3":"rgba(79,124,255,0.12)",
  "--ok":"#059669","--err":"#dc2626",
  "--sel-voice-bg":"transparent","--sel-voice-border":"#4f7cff","--sel-voice-tx":"#2952cc","--sel-voice-tag":"#2952cc",
  "--sel-av-bg":"rgba(79,124,255,0.1)","--sel-av-border":"#4f7cff","--sel-av-tx":"#2952cc",
  "--card-hover":"rgba(79,124,255,0.04)",
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Mono:ital,wght@0,400;0,500;1,400&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  html,body,#root{height:100%;overflow:hidden;background:var(--bg);color:var(--tx);font-family:var(--font)}
  button{cursor:pointer;border:none;background:none;color:inherit;font:inherit}
  input,textarea{font:inherit;color:inherit;border:none;outline:none;background:none}
  @keyframes spin{to{transform:rotate(360deg)}}
  @keyframes fadeIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}
  @keyframes slideUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
  ::-webkit-scrollbar{width:3px}
  ::-webkit-scrollbar-thumb{background:var(--b1);border-radius:2px}
  @keyframes pulse{0%,100%{transform:scaleY(0.4);opacity:0.4}50%{transform:scaleY(1);opacity:1}}
  @keyframes breathe{0%,100%{opacity:0.5;transform:scale(1)}50%{opacity:1;transform:scale(1.05)}}
  @keyframes dotPulse{0%,100%{opacity:1;box-shadow:0 0 0 0 var(--ok)}50%{opacity:.7;box-shadow:0 0 8px var(--ok)}}
  @keyframes soundPop{0%{transform:scale(0.7);opacity:0}60%{transform:scale(1.08)}100%{transform:scale(1);opacity:1}}
  @keyframes soundRipple{0%{box-shadow:0 0 0 0 var(--sc)}70%{box-shadow:0 0 0 8px transparent}100%{box-shadow:0 0 0 0 transparent}}
  @keyframes flashConnect{0%{opacity:0}20%{opacity:1}70%{opacity:0.6}100%{opacity:0}}
  @keyframes flashDisconnectBorder{0%{opacity:0}20%{opacity:1}70%{opacity:0.6}100%{opacity:0}}
  @keyframes ripple{0%{transform:scale(0.95);opacity:0.7}100%{transform:scale(1);opacity:1}}
  button:active{transform:scale(0.97)}

  /* Landing page keyframes */
  @keyframes landFadeUp{from{opacity:0;transform:translateY(32px)}to{opacity:1;transform:translateY(0)}}
  @keyframes landFadeIn{from{opacity:0}to{opacity:1}}
  @keyframes rippleExpand{from{transform:scale(1);opacity:0.7}to{transform:scale(3.5);opacity:0}}
  @keyframes travelRight{0%{transform:translateX(0);opacity:0}10%{opacity:1}85%{opacity:0.9}100%{transform:translateX(148px);opacity:0}}
  @keyframes bubbleAppear{0%,12%{opacity:0;transform:scale(0.8) translateY(4px)}25%,70%{opacity:1;transform:scale(1) translateY(0)}85%,100%{opacity:0;transform:scale(0.9) translateY(-3px)}}
  @keyframes glowPulse{0%,100%{box-shadow:0 0 30px rgba(79,124,255,0.4),0 6px 30px rgba(0,0,0,0.5),inset 0 1px 0 rgba(255,255,255,0.08)}50%{box-shadow:0 0 55px rgba(79,124,255,0.65),0 6px 30px rgba(0,0,0,0.5),inset 0 1px 0 rgba(255,255,255,0.08)}}
  @keyframes bgFloat{0%,100%{opacity:0.5;transform:scale(1) translateY(0)}50%{opacity:0.9;transform:scale(1.15) translateY(-10px)}}
  @keyframes bgFloatSlow{0%,100%{opacity:0.3;transform:scale(1) translateY(0)}50%{opacity:0.6;transform:scale(1.1) translateY(8px)}}
  @keyframes stageIn{from{opacity:0;transform:translateY(22px) scale(0.98)}to{opacity:1;transform:translateY(0) scale(1)}}
  @keyframes stageOut{from{opacity:1;transform:translateY(0) scale(1)}to{opacity:0;transform:translateY(-14px) scale(0.97)}}
  @keyframes nodeBreath{0%,100%{transform:scale(1);filter:brightness(1)}50%{transform:scale(1.08);filter:brightness(1.3)}}
  @keyframes scanLine{0%{transform:translateY(-100%);opacity:0}20%{opacity:1}80%{opacity:0.5}100%{transform:translateY(100%);opacity:0}}
  @keyframes tagFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-4px)}}

  .land-item-0{animation:landFadeUp 0.9s cubic-bezier(0.16,1,0.3,1) both 0.1s}
  .land-item-1{animation:landFadeUp 0.9s cubic-bezier(0.16,1,0.3,1) both 0.35s}
  .land-item-2{animation:landFadeUp 0.9s cubic-bezier(0.16,1,0.3,1) both 0.6s}
  .land-item-3{animation:landFadeUp 0.9s cubic-bezier(0.16,1,0.3,1) both 0.85s}
  .land-item-4{animation:landFadeUp 0.9s cubic-bezier(0.16,1,0.3,1) both 1.05s}
  .land-item-5{animation:landFadeUp 0.9s cubic-bezier(0.16,1,0.3,1) both 1.2s}

  .ripple-ring{transform-box:fill-box;transform-origin:center;animation:rippleExpand 2.1s ease-out infinite}
  .travel-dot{animation:travelRight 2s ease-in-out infinite;opacity:0}
  .relay-bubble{transform-box:fill-box;transform-origin:left center;animation:bubbleAppear 3s ease-in-out infinite}

  .stage-enter{animation:stageIn 0.45s cubic-bezier(0.16,1,0.3,1) both}
  .stage-exit{animation:stageOut 0.3s ease both}

  .cta-btn{animation:glowPulse 3s ease-in-out infinite}
  .cta-btn:hover{transform:scale(1.02)!important;transition:transform 0.15s}
  .cta-btn:active{transform:scale(0.98)!important}

  .choice-card:hover{border-color:#4f7cff!important;transform:translateY(-3px);transition:all 0.2s}
  .choice-card{transition:all 0.2s}

  /* ── UI POLISH ───────────────────────────── */

  /* Screen transitions */
  @keyframes screenSlideIn{from{opacity:0;transform:translateX(40px)}to{opacity:1;transform:translateX(0)}}
  @keyframes screenSlideOut{from{opacity:1;transform:translateX(0)}to{opacity:0;transform:translateX(-40px)}}
  .screen-enter{animation:screenSlideIn 0.35s cubic-bezier(0.16,1,0.3,1) both}

  /* Dial pad press */
  @keyframes dialPress{0%{transform:scale(1)}40%{transform:scale(0.92);background:var(--ac3)}100%{transform:scale(1)}}
  .dial-key:active{animation:dialPress 0.2s ease both}
  .dial-key{transition:all 0.1s}
  .dial-key:hover{background:var(--s2)!important;border-color:var(--ac)!important}

  /* Call button shine sweep */
  @keyframes shineSweep{0%{left:-100%}100%{left:200%}}
  .call-btn-active{position:relative;overflow:hidden}
  .call-btn-active::after{content:'';position:absolute;top:0;left:-100%;width:50%;height:100%;background:linear-gradient(90deg,transparent,rgba(255,255,255,0.15),transparent);animation:shineSweep 3s ease-in-out infinite}

  /* Transcript bubble entrance */
  @keyframes bubbleSlideLeft{from{opacity:0;transform:translateX(-20px)}to{opacity:1;transform:translateX(0)}}
  @keyframes bubbleSlideRight{from{opacity:0;transform:translateX(20px)}to{opacity:1;transform:translateX(0)}}
  @keyframes bubbleCenter{from{opacity:0;transform:scale(0.9)}to{opacity:1;transform:scale(1)}}
  .bubble-other{animation:bubbleSlideLeft 0.3s cubic-bezier(0.16,1,0.3,1) both}
  .bubble-user{animation:bubbleSlideRight 0.3s cubic-bezier(0.16,1,0.3,1) both}
  .bubble-center{animation:bubbleCenter 0.25s ease both}

  /* Connecting ripple */
  @keyframes connectRipple{0%{transform:scale(0.8);opacity:0.6;box-shadow:0 0 0 0 var(--ac)}50%{transform:scale(1);opacity:1}100%{transform:scale(0.8);opacity:0.6;box-shadow:0 0 20px 4px transparent}}
  .connecting-avatar{animation:connectRipple 1.5s ease-in-out infinite}

  /* Tab crossfade */
  @keyframes tabFadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
  .tab-content{animation:tabFadeIn 0.25s ease both}

  /* Phone input glow */
  .phone-input:focus{border-color:var(--ac)!important;box-shadow:0 0 0 3px rgba(79,124,255,0.15)!important}
  .phone-input{transition:border-color 0.2s,box-shadow 0.2s}

  /* Voice card selected glow */
  @keyframes voiceGlow{0%,100%{box-shadow:0 0 0 0 transparent}50%{box-shadow:0 0 12px rgba(79,124,255,0.2)}}
  .voice-selected{animation:voiceGlow 2.5s ease-in-out infinite}

  /* Mood dot pulse */
  @keyframes moodPulse{0%,100%{transform:scale(1)}50%{transform:scale(1.4)}}
  .mood-dot{animation:moodPulse 1.5s ease-in-out 1}
`;

function applyTheme(vars) {
  const root = document.documentElement;
  Object.entries(vars).forEach(([k,v]) => root.style.setProperty(k, v));
  root.style.setProperty("--font", "system-ui,-apple-system,'Segoe UI',sans-serif");
  root.style.setProperty("--mono", "'JetBrains Mono','Fira Code',monospace");
}

function fmt(n) {
  const d = n.replace(/\D/g,"");
  if(d.length<=3) return d;
  if(d.length<=6) return `(${d.slice(0,3)}) ${d.slice(3)}`;
  return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6,10)}`;
}
function fmtTime(s) {
  return `${String(Math.floor(s/60)).padStart(2,"0")}:${String(s%60).padStart(2,"0")}`;
}


// LandingWrap moved to components/LandingFlow.jsx


// ──────────────────────────────────────────────────────────────────
// TemplateEditCard
//
// One row in Settings → Templates & Shortcuts. Collapsed state shows
// icon + name + preview. Expanded state shows editable inputs for all
// four briefing fields plus phone. Props:
//   tpl          merged template object (with userRowId, isCustom)
//   expanded     bool — is this card currently open for editing
//   editBuffer   current edit state (shared across all cards but only
//                one expanded at a time, so no collision)
//   setEditBuffer  setter for editBuffer
//   saving       bool — whether a save is in flight (disables buttons)
//   onToggle     expand/collapse click
//   onSave       persist the edit buffer
//   onReset      null, or function to delete override (globals only)
//   onDelete     null, or function to delete (customs only)
//   authUser     nullable — disables editing when not signed in
// ──────────────────────────────────────────────────────────────────
function TemplateEditCard({ tpl, expanded, editBuffer, setEditBuffer, saving, onToggle, onSave, onReset, onDelete, authUser }) {
  const personalized = !!tpl.userRowId;
  const field = (key, label, placeholder, multiline = false) => {
    const val = editBuffer?.[key] ?? "";
    const commonStyle = {
      width: "100%",
      fontSize: 12,
      color: "var(--tx)",
      background: "var(--s1)",
      border: "1px solid var(--s3)",
      borderRadius: 8,
      padding: "7px 10px",
      caretColor: "var(--ac)",
      fontFamily: "inherit",
    };
    return (
      <div style={{display:"flex",flexDirection:"column",gap:3}}>
        <div style={{fontSize:9,fontFamily:"var(--mono)",fontWeight:700,color:"var(--tx3)",letterSpacing:"0.08em"}}>{label}</div>
        {multiline ? (
          <textarea
            value={val}
            onChange={e => setEditBuffer(b => ({...(b||{}), [key]: e.target.value}))}
            placeholder={placeholder}
            rows={2}
            style={{...commonStyle, resize:"none", lineHeight:1.4}}
          />
        ) : (
          <input
            value={val}
            onChange={e => setEditBuffer(b => ({...(b||{}), [key]: e.target.value}))}
            placeholder={placeholder}
            style={commonStyle}
          />
        )}
      </div>
    );
  };

  return (
    <div style={{background:"var(--s2)",border:`1px solid ${personalized?"var(--ac)":"var(--s3)"}`,borderRadius:9,overflow:"hidden"}}>
      {/* Collapsed header — always visible */}
      <button
        onClick={onToggle}
        style={{width:"100%",display:"flex",alignItems:"center",gap:10,padding:"8px 10px",background:"transparent",border:"none",textAlign:"left",cursor:"pointer"}}
      >
        <div style={{fontSize:18,lineHeight:1,flexShrink:0}}>{tpl.icon}</div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{display:"flex",alignItems:"center",gap:6}}>
            <div style={{fontSize:12,fontWeight:700,color:"var(--tx)",lineHeight:1.2}}>{tpl.name}</div>
            {personalized && !tpl.isCustom && (
              <span style={{fontSize:8,fontFamily:"var(--mono)",fontWeight:700,color:"var(--ac2)",background:"var(--ac3)",border:"1px solid var(--ac)",borderRadius:4,padding:"1px 5px",letterSpacing:"0.06em"}}>PERSONALIZED</span>
            )}
          </div>
          <div style={{fontSize:10,color:"var(--tx3)",lineHeight:1.4,fontFamily:"var(--mono)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
            {tpl.isCustom ? "My template" : tpl.category} · {tpl.goal || "(no goal)"}
          </div>
        </div>
        <div style={{fontSize:14,color:"var(--tx3)",transform:expanded?"rotate(180deg)":"rotate(0)",transition:"transform .2s",flexShrink:0}}>▾</div>
      </button>

      {/* Expanded edit area */}
      {expanded && editBuffer && (
        <div style={{padding:"10px 10px 12px",borderTop:"1px solid var(--s3)",display:"flex",flexDirection:"column",gap:8}}>
          {!authUser && (
            <div style={{fontSize:11,color:"#f59e0b",fontFamily:"var(--mono)",padding:"8px",background:"rgba(245,158,11,0.1)",borderRadius:6}}>
              ⚠ Sign in to save your edits.
            </div>
          )}
          {tpl.isCustom && field("name", "TEMPLATE NAME", "e.g. Dr. Smith's Office")}
          {field("who", "WHO", "e.g. Dr. Smith's dental office")}
          {field("goal", "GOAL", "e.g. Reschedule Thursday 2pm appointment")}
          {field("context", "CONTEXT", "Your name, DOB, account #, Rx #, etc.", true)}
          {field("instructions", "INSTRUCTIONS", "Fallbacks & preferences", true)}
          <div style={{display:"flex",flexDirection:"column",gap:3}}>
            <div style={{fontSize:9,fontFamily:"var(--mono)",fontWeight:700,color:"var(--tx3)",letterSpacing:"0.08em"}}>PHONE (OPTIONAL — AUTO-FILLS DIAL PAD)</div>
            <input
              value={editBuffer.phone || ""}
              onChange={e => {
                const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
                setEditBuffer(b => ({...(b||{}), phone: digits}));
              }}
              placeholder="2405551234"
              inputMode="numeric"
              style={{width:"100%",fontSize:12,color:"var(--tx)",background:"var(--s1)",border:"1px solid var(--s3)",borderRadius:8,padding:"7px 10px",caretColor:"var(--ac)",fontFamily:"var(--mono)"}}
            />
          </div>

          {/* Actions */}
          <div style={{display:"flex",gap:6,marginTop:4,flexWrap:"wrap"}}>
            <button
              onClick={onSave}
              disabled={saving || !authUser}
              style={{flex:"1 1 120px",padding:"8px 12px",borderRadius:8,background:"var(--ac)",color:"#fff",fontSize:12,fontWeight:700,border:"none",cursor:saving||!authUser?"not-allowed":"pointer",opacity:saving||!authUser?0.5:1}}
            >
              {saving ? "Saving…" : "Save"}
            </button>
            {onReset && (
              <button
                onClick={onReset}
                disabled={saving}
                style={{padding:"8px 12px",borderRadius:8,background:"var(--s1)",border:"1px solid var(--s3)",color:"var(--tx3)",fontSize:11,fontWeight:600,cursor:saving?"not-allowed":"pointer",opacity:saving?0.5:1}}
              >
                Reset to default
              </button>
            )}
            {onDelete && (
              <button
                onClick={onDelete}
                disabled={saving}
                style={{padding:"8px 12px",borderRadius:8,background:"rgba(244,63,94,0.1)",border:"1px solid rgba(244,63,94,0.3)",color:"#f43f5e",fontSize:11,fontWeight:600,cursor:saving?"not-allowed":"pointer",opacity:saving?0.5:1}}
              >
                Delete
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function App() {
  const [screen, setScreen]           = useState("home");
  const [dark, setDark]               = useState(true);
  const [avatarId, setAvatarId]       = useState("robot");

  useEffect(()=>{ applyTheme(dark ? DARK : LIGHT); }, [dark]);
  const [phone, setPhone]             = useState("");
  const [voice, setVoice]             = useState(VOICES[0]);
  const [briefing, setBriefing]       = useState("");
  const [callState, setCallState]     = useState("idle");
  const [callData, setCallData]       = useState(null);
  const [transcript, setTranscript]   = useState([]);
  const [input, setInput]             = useState("");
  const [duration, setDuration]       = useState(0);
  const [error, setError]             = useState(null);
  const [callMode, setCallMode]       = useState("manual");
  // Translator is now a capability (not a mode). Lives orthogonally to
  // callMode — both Manual and AI Agent can use it. Backend already keys
  // off targetLanguage presence rather than mode, so no backend change.
  const [translatorEnabled, setTranslatorEnabled] = useState(
    () => localStorage.getItem("mr_translator_on") === "1"
  );
  const [targetLanguage, setTargetLanguage] = useState(() => {
    const saved = localStorage.getItem("mr_target_lang");
    return LANGUAGES.find(l => l.code === saved) || LANGUAGES[0];
  });
  const [showLanguagePicker, setShowLanguagePicker] = useState(false);
  const saveTranslatorEnabled = (v) => {
    setTranslatorEnabled(v);
    localStorage.setItem("mr_translator_on", v ? "1" : "0");
  };
  const saveTargetLanguage = (lang) => {
    setTargetLanguage(lang);
    localStorage.setItem("mr_target_lang", lang.code);
  };
  const [agentWho, setAgentWho]       = useState("");
  const [agentGoal, setAgentGoal]     = useState("");
  const [agentContext, setAgentContext]     = useState("");
  const [agentInstructions, setAgentInstructions] = useState("");
  const [agentThinking, setAgentThinking] = useState(false);
  const [callerMood, setCallerMood]       = useState(null);
  const [isListening, setIsListening]     = useState(false);
  const recognitionRef = useRef(null);
  const [agentMode, setAgentMode]     = useState("agent");
  const endRef = useRef(null);
  const taRef  = useRef(null);

  // ── Settings state ────────────────────────
  const [userName, setUserName] = useState(() => localStorage.getItem("mr_user_name") || "");
  const saveUserName = (name) => { setUserName(name); localStorage.setItem("mr_user_name", name); syncPreferences({ userName: name }); };

  const DEFAULT_INTRO = userName
    ? `Hi, this is an AI relay call for ${userName}. Please speak normally.`
    : "Hi, this is an AI relay call. The person you're speaking with uses a text relay. Please speak normally.";
  const DEFAULT_SHORTCUTS = [
    { key: "/dob",   value: "" },
    { key: "/name",  value: userName ? `My name is ${userName}` : "" },
    { key: "/addr",  value: "" },
    { key: "/yes",   value: "Yes, that works for me." },
    { key: "/no",    value: "No, that does not work for me." },
    { key: "/hold",  value: "One moment please, I am reading your response." },
    { key: "/ty",    value: "Thank you very much!" },
    { key: "/bye",   value: "Thank you, have a great day. Goodbye." },
  ];
  const [introMsg, setIntroMsg]       = useState(() => localStorage.getItem("mr_intro") || DEFAULT_INTRO);
  const [introEnabled, setIntroEnabled] = useState(() => localStorage.getItem("mr_intro_on") !== "false");
  const [shortcutsEnabled, setShortcutsEnabled] = useState(() => localStorage.getItem("mr_shortcuts_on") !== "false");
  const [transcriptMode, setTranscriptMode] = useState(() => localStorage.getItem("mr_transcript_mode") || "ask");
  const [transcriptEmail, setTranscriptEmail] = useState(() => localStorage.getItem("mr_transcript_email") || "");
  const [shortcuts, setShortcuts]     = useState(() => {
    try { return JSON.parse(localStorage.getItem("mr_shortcuts")) || DEFAULT_SHORTCUTS; } catch { return DEFAULT_SHORTCUTS; }
  });
  const [newKey, setNewKey]           = useState("");
  const [newVal, setNewVal]           = useState("");
  const [shortcutSuggestion, setShortcutSuggestion] = useState(null);
  const [showTranscriptPrompt, setShowTranscriptPrompt] = useState(false);
  const [tab, setTab] = useState("dial");
  const [settingsSubTab, setSettingsSubTab] = useState("account");

  const [showLanding, setShowLanding] = useState(() => localStorage.getItem("mr_visited") !== "true");
  const [showPathChoice, setShowPathChoice] = useState(false);
  const [landingOut, setLandingOut] = useState(false);

  const handleGetStarted = () => {
    setLandingOut(true);
    setTimeout(() => {
      localStorage.setItem("mr_visited", "true");
      setShowLanding(false);
      setShowPathChoice(true);
    }, 600);
  };

  // ── AUTH STATE ──────────────────────────────────────
  const [authUser, setAuthUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  // Per-user template overrides + custom templates, fetched from Supabase on auth.
  const [userTemplates, setUserTemplates] = useState([]);

  // Re-fetch user templates — called on auth change, after save, after delete.
  const refreshUserTemplates = async () => {
    try {
      const rows = await listUserTemplates();
      setUserTemplates(rows);
    } catch (e) {
      console.error("[templates] fetch failed:", e);
      // Don't blow up the UI — fall back to empty list (pristine globals).
      setUserTemplates([]);
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      const u = session?.user ?? null;
      setAuthUser(u);
      setAuthLoading(false);
      if (u) {
        refreshUserTemplates();
        const hasAccess = localStorage.getItem(`mr_access_${u.id}`) === "granted";
        if (hasAccess) {
          setAccessGranted(true);
        } else {
          setLandingStage("choice");
        }
      }
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const u = session?.user ?? null;
      setAuthUser(u);
      if (u) {
        refreshUserTemplates();
        const hasAccess = localStorage.getItem(`mr_access_${u.id}`) === "granted";
        if (hasAccess) setAccessGranted(true);
        else setLandingStage("choice");
      } else {
        setUserTemplates([]);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    setAuthUser(null);
    setUserTemplates([]);
  };

  // ── Usage tracking ──────────────────────────
  const [usage, setUsage] = useState(null);
  useEffect(() => {
    if (!authUser) return;
    const fetchUsage = async () => {
      try {
        const res = await apiFetch("/api/calls/usage");
        const data = await res.json();
        setUsage(data);
      } catch(e) { console.warn("[Usage] fetch failed:", e.message); }
    };
    fetchUsage();
  }, [authUser]);

  // ── Load preferences from Supabase on login ──
  useEffect(() => {
    if (!authUser) return;
    const loadPrefs = async () => {
      try {
        const res = await apiFetch("/api/settings/preferences");
        const prefs = await res.json();
        if (!prefs || Object.keys(prefs).length === 0) return;
        // Apply Supabase prefs → state + localStorage
        if (prefs.userName) { setUserName(prefs.userName); localStorage.setItem("mr_user_name", prefs.userName); }
        if (prefs.introMsg) { setIntroMsg(prefs.introMsg); localStorage.setItem("mr_intro", prefs.introMsg); }
        if (prefs.introEnabled !== undefined) { setIntroEnabled(prefs.introEnabled); localStorage.setItem("mr_intro_on", prefs.introEnabled); }
        if (prefs.shortcutsEnabled !== undefined) { setShortcutsEnabled(prefs.shortcutsEnabled); localStorage.setItem("mr_shortcuts_on", prefs.shortcutsEnabled); }
        if (prefs.transcriptMode) { setTranscriptMode(prefs.transcriptMode); localStorage.setItem("mr_transcript_mode", prefs.transcriptMode); }
        if (prefs.transcriptEmail) { setTranscriptEmail(prefs.transcriptEmail); localStorage.setItem("mr_transcript_email", prefs.transcriptEmail); }
        if (prefs.shortcuts?.length) { setShortcuts(prefs.shortcuts); localStorage.setItem("mr_shortcuts", JSON.stringify(prefs.shortcuts)); }
        console.log("[Prefs] Loaded from Supabase");
      } catch(e) { console.warn("[Prefs] Load failed:", e.message); }
    };
    loadPrefs();
  }, [authUser]);

  // ── Call history ──────────────────────────
  const [callHistory, setCallHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await apiFetch("/api/calls");
      const data = await res.json();
      setCallHistory(Array.isArray(data) ? data : []);
    } catch(e) { console.warn("[History] fetch failed:", e.message); }
    finally { setHistoryLoading(false); }
  };

  const currentPath = window.location.pathname;
  const isProPath = currentPath === "/pro" || currentPath === "/pro/";
  const [accessGranted, setAccessGranted] = useState(false);
  const [accessCode, setAccessCode] = useState("");
  const [accessError, setAccessError] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [landingStage, setLandingStage] = useState("hero");
  const [landingExiting, setLandingExiting] = useState(false);

  const navigateLanding = (stage) => {
    setLandingExiting(true);
    setTimeout(() => {
      setLandingStage(stage);
      setLandingExiting(false);
    }, 280);
  };
  const submitAccessCode = async () => {
    try {
      const res = await apiFetch("/api/auth/verify-beta-code", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: accessCode }),
      });
      if (res.ok) {
        // Valid code — go to auth (Step 1) so account gets created first
        if (authUser) {
          localStorage.setItem(`mr_access_${authUser.id}`, "granted");
          setAccessGranted(true);
        } else {
          localStorage.setItem("mr_beta_code_ok", "true");
          setLandingStage("auth");
        }
      } else {
        setAccessError(true);
        setTimeout(() => setAccessError(false), 2000);
      }
    } catch (e) {
      setAccessError(true);
      setTimeout(() => setAccessError(false), 2000);
    }
  };

  const STRIPE_LINKS = {
    starter: "https://buy.stripe.com/cNi6oHbvOguW92Ya9u6J201",
    pro: "https://buy.stripe.com/00weVdczS6Um7YU3L66J202",
  };

  const handleStripeCheckout = (plan = "pro") => {
    // Pass user info to Stripe so webhook can link the subscription
    const url = new URL(STRIPE_LINKS[plan]);
    if (authUser?.id) url.searchParams.set("client_reference_id", authUser.id);
    if (authUser?.email) url.searchParams.set("prefilled_email", authUser.email);
    window.location.href = url.toString();
  };

  // Check subscription status from backend (not localStorage)
  useEffect(() => {
    const checkSubscription = async () => {
      // Handle Stripe return redirect
      const params = new URLSearchParams(window.location.search);
      if (params.get("payment") === "success") {
        window.history.replaceState({}, "", "/");
        // Give Stripe webhook a moment to process
        await new Promise(r => setTimeout(r, 2000));
      }

      // Check server-side subscription status
      if (!authUser) return;
      try {
        const res = await apiFetch("/api/stripe/subscription-status");
        const data = await res.json();
        if (data.active) {
          localStorage.setItem(`mr_access_${authUser.id}`, "granted");
          setAccessGranted(true);
          console.log(`[Stripe] Active subscription: ${data.plan}`);
        }
      } catch (e) {
        console.warn("[Stripe] Could not check subscription:", e.message);
      }
    };
    checkSubscription();
  }, [authUser]);
  const [complaint, setComplaint] = useState({ category:"audio_quality", description:"" });
  const [adminPin, setAdminPin] = useState("");
  const [adminUnlocked, setAdminUnlocked] = useState(false);
  const [adminComplaints, setAdminComplaints] = useState([]);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminResolution, setAdminResolution] = useState({});
  // Admin PIN verified via backend — no secrets in frontend

  const loadComplaints = async () => {
    setAdminLoading(true);
    try {
      const res = await apiFetch("/api/auth/complaints");
      const data = await res.json();
      setAdminComplaints(data);
    } catch(e) {} finally { setAdminLoading(false); }
  };

  const resolveComplaint = async (id, userEmail) => {
    const resolution = adminResolution[id];
    if (!resolution?.trim()) return;
    try {
      await apiFetch(`/api/auth/complaint/${id}/resolve`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ resolution, user_email: userEmail }),
      });
      loadComplaints();
      setAdminResolution(r => ({...r, [id]: ""}));
    } catch(e) {}
  };
  const [complaintSent, setComplaintSent] = useState(false);
  const [complaintLoading, setComplaintLoading] = useState(false);

  const submitComplaint = async () => {
    if (!complaint.description.trim()) return;
    setComplaintLoading(true);
    try {
      const res = await apiFetch("/api/auth/complaint", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ ...complaint, user_email: user?.email }),
      });
      if (res.ok) { setComplaintSent(true); setComplaint({ category:"audio_quality", description:"" }); }
    } catch(e) {} finally { setComplaintLoading(false); }
  };
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("mr_user")) || null; } catch { return null; }
  });
  const [showReg, setShowReg] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  // ID of the template card currently expanded for editing in Settings.
  // Matches the merged template's `id` field (global id or "custom-<uuid>").
  const [expandedTemplateId, setExpandedTemplateId] = useState(null);
  // Local edit buffer — populated when a card expands, flushed on save.
  const [templateEditBuffer, setTemplateEditBuffer] = useState(null);
  const [templateSaving, setTemplateSaving] = useState(false);
  const [regForm, setRegForm] = useState({
    email:"", full_name:"", phone:"",
    address_line1:"", address_line2:"", city:"", state:"", zip:"",
    location_type:"home", disability_type:"deaf",
    terms_accepted:false, privacy_accepted:false,
  });
  const [regError, setRegError] = useState("");
  const [regLoading, setRegLoading] = useState(false);

  const saveUser = (u) => { setUser(u); localStorage.setItem("mr_user", JSON.stringify(u)); };

  const submitRegistration = async () => {
    setRegError(""); setRegLoading(true);
    try {
      const res = await apiFetch("/api/auth/register", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify(regForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Registration failed");
      saveUser({ email: regForm.email, full_name: regForm.full_name, city: regForm.city, state: regForm.state, userId: data.userId });
      setShowReg(false);
    } catch(e) {
      setRegError(e.message);
    } finally {
      setRegLoading(false);
    }
  };
  const [dbgCount, setDbgCount] = useState(0);
  const [flashEffect, setFlashEffect] = useState(null); // "connect" | "disconnect" | null

  const [flashKey, setFlashKey] = useState(0);
  const triggerFlash = (type) => {
    setFlashEffect(null);
    setTimeout(() => {
      setFlashEffect(type);
      setFlashKey(k => k + 1);
      setTimeout(() => setFlashEffect(null), 900);
    }, 20);
  };

  // ── Sync preferences to Supabase (debounced) ──
  const syncTimer = useRef(null);
  const syncPreferences = (overrides = {}) => {
    clearTimeout(syncTimer.current);
    syncTimer.current = setTimeout(() => {
      if (!authUser) return;
      const prefs = {
        userName: localStorage.getItem("mr_user_name") || "",
        introMsg: localStorage.getItem("mr_intro") || "",
        introEnabled: localStorage.getItem("mr_intro_on") !== "false",
        shortcutsEnabled: localStorage.getItem("mr_shortcuts_on") !== "false",
        transcriptMode: localStorage.getItem("mr_transcript_mode") || "ask",
        transcriptEmail: localStorage.getItem("mr_transcript_email") || "",
        shortcuts: JSON.parse(localStorage.getItem("mr_shortcuts") || "[]"),
        ...overrides,
      };
      apiFetch("/api/settings/preferences", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ preferences: prefs }),
      }).catch(e => console.warn("[Prefs] Sync failed:", e.message));
    }, 1500);
  };

  const saveIntro = (val) => { setIntroMsg(val); localStorage.setItem("mr_intro", val); syncPreferences({ introMsg: val }); };
  const saveIntroEnabled = (v) => { setIntroEnabled(v); localStorage.setItem("mr_intro_on", v); syncPreferences({ introEnabled: v }); };
  const saveShortcutsEnabled = (v) => { setShortcutsEnabled(v); localStorage.setItem("mr_shortcuts_on", v); syncPreferences({ shortcutsEnabled: v }); };
  const saveTranscriptMode = (v) => { setTranscriptMode(v); localStorage.setItem("mr_transcript_mode", v); syncPreferences({ transcriptMode: v }); };
  const saveTranscriptEmail = (v) => { setTranscriptEmail(v); localStorage.setItem("mr_transcript_email", v); syncPreferences({ transcriptEmail: v }); };
  const saveShortcuts = (arr) => { setShortcuts(arr); localStorage.setItem("mr_shortcuts", JSON.stringify(arr)); syncPreferences({ shortcuts: arr }); };
  const addShortcut = () => {
    if (!newKey.startsWith("/") || !newVal.trim()) return;
    const arr = [...shortcuts.filter(s => s.key !== newKey), { key: newKey.toLowerCase(), value: newVal.trim() }];
    saveShortcuts(arr); setNewKey(""); setNewVal("");
  };
  const deleteShortcut = (key) => saveShortcuts(shortcuts.filter(s => s.key !== key));
  const updateShortcutVal = (key, val) => saveShortcuts(shortcuts.map(s => s.key===key ? {...s,value:val} : s));

  // Detect shortcut in input and show suggestion
  const handleInputChange = (val) => {
    setInput(val);
    const words = val.split(" ");
    const last = words[words.length - 1];
    if (shortcutsEnabled && last.startsWith("/") && last.length > 1) {
      const match = shortcuts.find(s => s.key === last && s.value);
      setShortcutSuggestion(match || null);
    } else {
      setShortcutSuggestion(null);
    }
  };
  const applyShortcut = () => {
    if (!shortcutSuggestion) return;
    const words = input.split(" ");
    words[words.length - 1] = shortcutSuggestion.value;
    setInput(words.join(" "));
    setShortcutSuggestion(null);
  };

  const buildTranscriptText = (ts) => {
    const lines = ts.filter(l=>l.speaker!=="intro").map(l=>{
      const who = l.speaker==="user"?"You":"Caller";
      return `[${who}] ${l.text}`;
    });
    return `MyRelay.ai Call Transcript\nDate: ${new Date().toLocaleString()}\n\n` + lines.join("\n");
  };

  const downloadTranscript = () => {
    const text = buildTranscriptText(transcript);
    const blob = new Blob([text], {type:"text/plain"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `relay-transcript-${Date.now()}.txt`;
    a.click(); URL.revokeObjectURL(url);
  };

  const downloadTranscriptPDF = () => {
    const lines = transcript.filter(l=>l.speaker!=="intro").map(l=>{
      const who = l.speaker==="user"?"You":"Caller";
      return `[${who}] ${l.text}`;
    });
    const date = new Date().toLocaleString();
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>MyRelay Transcript</title>
    <style>body{font-family:system-ui,sans-serif;max-width:700px;margin:40px auto;color:#1a1a2e;line-height:1.6}
    h1{color:#4f7cff;font-size:22px}h2{color:#5a7090;font-size:14px;font-weight:400;margin-top:4px}
    .line{padding:8px 14px;margin:6px 0;border-radius:10px;font-size:15px}
    .you{background:#dce8ff;color:#2952cc;text-align:right;margin-left:20%}
    .caller{background:#f0f4ff;color:#0f1623;margin-right:20%}
    footer{margin-top:40px;font-size:11px;color:#9aabc2;text-align:center}</style></head>
    <body><h1>MyRelay.ai Call Transcript</h1><h2>${date}</h2><br>
    ${lines.map(l=>l.startsWith('[You]')?`<div class="line you">${l.replace('[You] ','')}</div>`:`<div class="line caller">${l.replace('[Caller] ','')}</div>`).join('')}
    <footer>Generated by MyRelay.ai — AI Phone Relay for the Deaf Community</footer></body></html>`;
    const blob = new Blob([html], {type:"text/html"});
    const url = URL.createObjectURL(blob);
    const w = window.open(url, "_blank");
    setTimeout(()=>{ w?.print(); URL.revokeObjectURL(url); }, 500);
  };

  const emailTranscript = async (overrideEmail) => {
    const to = overrideEmail || transcriptEmail || "";
    if (!to) return;
    try {
      const res = await apiFetch("/api/calls/transcript-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: to,
          transcript: transcript.filter(l => l.speaker !== "intro" && l.speaker !== "sound"),
          callDate: new Date().toISOString(),
          duration,
          phoneNumber: phone,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setTranscript(t => [...t, { speaker: "intro", text: `Transcript emailed to ${to}` }]);
      } else {
        // Fallback to mailto if server email isn't configured
        const text = buildTranscriptText(transcript);
        const subject = encodeURIComponent("MyRelay.ai Call Transcript");
        const body = encodeURIComponent(text);
        window.open(`mailto:${to}?subject=${subject}&body=${body}`);
      }
    } catch (e) {
      // Fallback to mailto on any error
      const text = buildTranscriptText(transcript);
      const subject = encodeURIComponent("MyRelay.ai Call Transcript");
      const body = encodeURIComponent(text);
      window.open(`mailto:${to}?subject=${subject}&body=${body}`);
    }
  };

  const handleTranscriptAfterCall = () => {
    if (transcriptMode === "off") return;
    if (transcriptMode === "always") {
      if (transcriptEmail) { emailTranscript(transcriptEmail); } else { downloadTranscriptPDF(); }
      return;
    }
    if (transcriptMode === "ask") setShowTranscriptPrompt(true);
  };

  const canCall = phone.replace(/\D/g,"").length >= 10;

  useEffect(()=>{
    if(screen!=="call") return;
    const t = setInterval(()=>setDuration(d=>d+1),1000);
    return()=>clearInterval(t);
  },[screen]);

  useEffect(()=>{ endRef.current?.scrollIntoView({behavior:"smooth"}); },[transcript]);

  const socketRef = useRef(null);

  const startCall = async () => {
    if(!canCall) return;
    if(!authUser) {
      setAccessGranted(false);
      setLandingStage("auth");
      return;
    }
    const e164 = "+1" + phone.replace(/\D/g,"");
    setScreen("call");
    setCallState("connecting");
    setTranscript([]);
    setDuration(0);
    setError(null);

    // Probe backend before opening socket — ensures API_URL points to a live server
    try { await apiFetch("/health"); } catch(e) {
      console.warn("[startCall] Health check failed, proceeding with API_URL:", API_URL);
    }

    const socket = io(API_URL, { transports: ["websocket"] });
    socketRef.current = socket;

    socket.on("call:connected", ({ callId }) => {
      setCallState("active");
      triggerFlash("connect");
      setCallData(d => ({ ...d, callId }));
      setTranscript(t => [...t, {
        speaker: "intro",
        text: callMode === "agent" ? "AI Agent is handling the call. Watch the transcript." : "Call connected. Start typing to speak."
      }]);
    });

    socket.on("agent:thinking", () => setAgentThinking(true));
    socket.on("agent:speaking", ({ text }) => {
      setAgentThinking(false);
      setTranscript(t => [...t, { speaker: "user", text, isAgent: true }]);
    });
    socket.on("agent:goal_complete", () => {
      setTranscript(t => [...t, { speaker: "intro", text: "Goal completed." }]);
    });
    socket.on("agent:error", ({ message }) => setError("Agent error: " + message));
    socket.on("call:sound_event", ({ event }) => {
      setTranscript(t => [...t, { speaker: "sound", text: event }]);
    });
    // Diarization: backend fires this the first time a 2nd, 3rd, etc.
    // speaker is detected on the call. Surface an inline note in the
    // transcript so the user knows a new person joined/took the phone.
    socket.on("call:speaker_change", ({ speaker, totalSpeakers }) => {
      const friendly = speaker ? speaker.replace("speaker_", "Speaker ") : "Someone new";
      setTranscript(t => [...t, {
        speaker: "intro",
        text: `${friendly} joined the call (${totalSpeakers} voices detected)`,
      }]);
    });
    socket.on("call:mood", ({ mood }) => {
      setCallerMood(mood);
      setTranscript(t => {
        const updated = [...t];
        for (let i = updated.length - 1; i >= 0; i--) {
          if (updated[i].speaker === "other") {
            updated[i] = { ...updated[i], mood };
            break;
          }
        }
        return updated;
      });
    });

    socket.on("transcript:incoming", ({ text, isFinal, translating, speakerId }) => {
      setDbgCount(c => c + 1);
      if(!text) return;
      setTranscript(t => {
        const last = t[t.length - 1];
        if(last && last.speaker === "other" && !last.final) {
          return [...t.slice(0,-1), { speaker:"other", text, final: isFinal, translating, speakerId }];
        }
        return [...t, { speaker:"other", text, final: isFinal, translating, speakerId }];
      });
    });

    // Translation results — update transcript with translated text
    socket.on("transcript:translated", ({ original, translated, speaker }) => {
      if (speaker === "other") {
        // Replace the last "other" bubble with translation
        setTranscript(t => {
          const updated = [...t];
          for (let i = updated.length - 1; i >= 0; i--) {
            if (updated[i].speaker === "other" && (updated[i].text === original || updated[i].translating)) {
              updated[i] = { ...updated[i], text: translated, originalText: original, final: true, translating: false };
              break;
            }
          }
          return updated;
        });
      } else if (speaker === "user") {
        // Show what was actually spoken in target language
        setTranscript(t => {
          const updated = [...t];
          for (let i = updated.length - 1; i >= 0; i--) {
            if (updated[i].speaker === "user" && updated[i].text === original) {
              updated[i] = { ...updated[i], translatedText: translated };
              break;
            }
          }
          return updated;
        });
      }
    });

    socket.on("call:status", ({ status }) => {
      if(status === "completed" || status === "failed") {
        triggerFlash("disconnect");
        setCallState("ended");
      } else if(status === "in-progress") {
        setCallState("active");
      } else if(status === "ringing") {
        setCallState("ringing");
      }
    });

    socket.on("call:intro", ({ text }) => {
      setTranscript(t => [...t, { speaker: "user", text, isIntro: true }]);
    });
    socket.on("call:error", ({ message }) => setError(message));

    try {
      const res = await apiFetch("/api/calls/start",{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({
          phoneNumber: e164,
          voiceId: voice.id,
          mode: callMode,
          userName: userName || undefined,
          // Translator is now orthogonal to mode — fires whenever the user
          // has enabled it, in either Manual or AI Agent. Backend keys off
          // targetLanguage presence (not mode), so this works without a
          // backend change.
          targetLanguage: translatorEnabled ? targetLanguage.code : undefined,
          introMessage: introEnabled ? introMsg : null,
          briefing: callMode === "agent"
            ? { who: agentWho, goal: agentGoal, context: agentContext, instructions: agentInstructions }
            : { purpose: briefing },
        }),
      });
      const data = await res.json();
      if(data.emergency) {
        setError(data.message);
        setCallState("error");
        socket.disconnect();
        return;
      }
      if(!res.ok) throw new Error(data.error||"Call failed");
      setCallData(data);
      setCallState("ringing");
    } catch(e) {
      const msg = e.message === "Failed to fetch"
        ? `Cannot reach server (${API_URL}). Check your connection or try again.`
        : e.message;
      console.error("[startCall] Error:", e.message, "API_URL:", API_URL);
      setError(msg);
      setCallState("error");
      socket.disconnect();
    }
  };

  const takeOver = async () => {
    setAgentMode("manual");
    try {
      await apiFetch("/api/calls/agent-takeover", {
        method: "POST", headers: {"Content-Type":"application/json"},
        body: JSON.stringify({ callId: callData?.callId }),
      });
    } catch(e) {}
  };

  const handBack = async () => {
    setAgentMode("agent");
    try {
      await apiFetch("/api/calls/agent-resume", {
        method: "POST", headers: {"Content-Type":"application/json"},
        body: JSON.stringify({ callId: callData?.callId }),
      });
    } catch(e) {}
  };

  const startListening = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) { setError("Speech recognition not supported in this browser."); return; }
    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = "en-US";
    recognitionRef.current = recognition;
    recognition.onstart = () => setIsListening(true);
    recognition.onresult = (e) => {
      const transcript = Array.from(e.results).map(r => r[0].transcript).join("");
      setInput(transcript);
    };
    recognition.onend = () => setIsListening(false);
    recognition.onerror = (e) => { setIsListening(false); console.error("Speech error:", e.error); };
    recognition.start();
  };

  const stopListening = () => {
    recognitionRef.current?.stop();
    setIsListening(false);
  };

  const sendText = async () => {
    if(!input.trim()) return;
    const text = input.trim();
    setInput("");
    setTranscript(t=>[...t,{speaker:"user",text}]);
    try {
      await apiFetch("/api/calls/speak",{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({ callId:callData?.callId, text, voiceId:voice.id }),
      });
    } catch(e) { console.error(e); }
    taRef.current?.focus();
  };

  const endCall = async () => {
    try {
      if(callData?.callId) await apiFetch("/api/calls/hangup",{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({ callId:callData.callId }),
      });
    } catch(e) {}
    if(socketRef.current) { socketRef.current.disconnect(); socketRef.current = null; }
    handleTranscriptAfterCall();
    setScreen("home");
    setCallState("idle");
    setCallData(null);
    setPhone("");
    setBriefing("");
    // Refresh usage and history after call
    apiFetch("/api/calls/usage").then(r => r.json()).then(setUsage).catch(() => {});
    fetchHistory();
  };

  const dialKey = (k) => { if(phone.replace(/\D/g,"").length<10) setPhone(p=>p+k); };

  // Merge global templates with the user's personalizations and custom
  // templates. Recomputed on every render — cheap, stable across sessions
  // because both inputs come from state refs that only change on save/delete.
  const mergedTemplates = mergeTemplates(GLOBAL_TEMPLATES, userTemplates);

  // Apply a briefing template to the AI Agent fields. If the template carries
  // a phone number AND the dial input is empty, also populate the dial.
  // Never clobbers a phone number the user has already typed.
  const applyTemplate = (tpl) => {
    setAgentWho(tpl.who || "");
    setAgentGoal(tpl.goal || "");
    setAgentContext(tpl.context || "");
    setAgentInstructions(tpl.instructions || "");
    if (tpl.phone && !phone) {
      setPhone(String(tpl.phone).replace(/\D/g, "").slice(0, 10));
    }
    setShowTemplatePicker(false);
  };

  // Save the current briefing fields as a new user template. Prompts for a name.
  const saveCurrentAsTemplate = async () => {
    if (!authUser) {
      alert("Sign in to save templates.");
      return;
    }
    const name = prompt("Name this template (e.g. \"Dr. Smith's Office\"):");
    if (!name?.trim()) return;
    try {
      await createUserTemplate({
        name: name.trim(),
        icon: "📋",
        who: agentWho,
        goal: agentGoal,
        context: agentContext,
        instructions: agentInstructions,
        phone: phone,
      });
      await refreshUserTemplates();
    } catch (e) {
      console.error("[templates] save failed:", e);
      alert("Could not save template: " + (e.message || "unknown error"));
    }
  };

  // Expand a template card in Settings for editing. Populates the edit buffer
  // with the template's current (possibly already-personalized) values.
  const expandTemplate = (tpl) => {
    if (expandedTemplateId === tpl.id) {
      setExpandedTemplateId(null);
      setTemplateEditBuffer(null);
      return;
    }
    setExpandedTemplateId(tpl.id);
    setTemplateEditBuffer({
      name: tpl.name,
      icon: tpl.icon,
      who: tpl.who,
      goal: tpl.goal,
      context: tpl.context,
      instructions: tpl.instructions,
      phone: tpl.phone || "",
    });
  };

  // Save the edit buffer. Custom templates → update by userRowId.
  // Global overrides → upsert via global_id. Pristine globals → first-time override.
  const persistTemplateEdit = async (tpl) => {
    if (!authUser) {
      alert("Sign in to save templates.");
      return;
    }
    if (!templateEditBuffer) return;
    setTemplateSaving(true);
    try {
      if (tpl.isCustom) {
        // Custom template: plain update by row id.
        await updateUserTemplate(tpl.userRowId, templateEditBuffer);
      } else {
        // Global template: upsert an override keyed by global_id.
        // (If already exists, it updates; otherwise creates.)
        await saveGlobalOverride(tpl.id, templateEditBuffer);
      }
      await refreshUserTemplates();
      setExpandedTemplateId(null);
      setTemplateEditBuffer(null);
    } catch (e) {
      console.error("[templates] edit save failed:", e);
      alert("Could not save changes: " + (e.message || "unknown error"));
    } finally {
      setTemplateSaving(false);
    }
  };

  // Reset a global template to its built-in defaults by deleting the override row.
  const resetGlobalTemplate = async (tpl) => {
    if (!tpl.userRowId) return;  // already pristine
    if (!confirm(`Reset "${tpl.name}" to the built-in default? Your personalization will be lost.`)) return;
    try {
      await deleteUserTemplate(tpl.userRowId);
      await refreshUserTemplates();
      setExpandedTemplateId(null);
      setTemplateEditBuffer(null);
    } catch (e) {
      console.error("[templates] reset failed:", e);
      alert("Could not reset: " + (e.message || "unknown error"));
    }
  };

  // Delete a custom (non-global) user template.
  const removeCustomTemplate = async (tpl) => {
    if (!tpl.isCustom || !tpl.userRowId) return;
    if (!confirm(`Delete "${tpl.name}"? This cannot be undone.`)) return;
    try {
      await deleteUserTemplate(tpl.userRowId);
      await refreshUserTemplates();
      setExpandedTemplateId(null);
      setTemplateEditBuffer(null);
    } catch (e) {
      console.error("[templates] delete failed:", e);
      alert("Could not delete: " + (e.message || "unknown error"));
    }
  };

  // ── Shared styles ─────────────────────────
  const S = {
    label: {fontSize:10,fontFamily:"var(--mono)",color:"var(--tx3)",letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:6},
    card: {background:"var(--s1)",border:"1px solid var(--s3)",borderRadius:14},
  };

  // ── LEGAL PAGES ──────────────────────────────────────────
  const [legalPage, setLegalPage] = useState(() => {
    const path = window.location.pathname;
    if (path === "/privacy") return "privacy";
    if (path === "/terms") return "terms";
    if (path === "/opi") return "opi";
    if (path === "/interpret" || path === "/interpret/") return "interpret";
    return null;
  });

  if (legalPage === "interpret") return (
    <div style={{width:"100%",height:"100vh",background:"transparent"}}>
      <InterpreterModal open={true} onClose={() => {
        // Parent page (iframe host) should handle close — send postMessage
        try { window.parent.postMessage({ type: "myrelay:close" }, "*"); } catch {}
        setLegalPage(null);
        window.history.pushState({}, "", "/");
      }}/>
    </div>
  );

  if (legalPage === "opi") return (
    <div style={{width:"100%",height:"100vh",maxWidth:480,margin:"0 auto"}}>
      <OPIWidget embedded onClose={() => { setLegalPage(null); window.history.pushState({}, "", "/"); }}/>
    </div>
  );
  if (legalPage === "privacy") return <PrivacyPolicy onBack={() => { setLegalPage(null); window.history.pushState({}, "", "/"); }} />;
  if (legalPage === "terms") return <TermsOfService onBack={() => { setLegalPage(null); window.history.pushState({}, "", "/"); }} />;

  // ── CALL SCREEN ─────────────────────────────────────────
  if(screen==="call") {
    return (
      <CallScreen
        callState={callState} callMode={callMode} agentMode={agentMode} agentThinking={agentThinking}
        transcript={transcript} input={input} error={error} duration={duration} isListening={isListening}
        flashEffect={flashEffect} flashKey={flashKey} shortcutSuggestion={shortcutSuggestion}
        avatarId={avatarId} css={css}
        translatorEnabled={translatorEnabled} targetLanguage={targetLanguage}
        sendText={sendText} endCall={endCall} handleInputChange={handleInputChange}
        applyShortcut={applyShortcut} takeOver={takeOver} handBack={handBack}
        startListening={startListening} stopListening={stopListening} setError={setError}
        endRef={endRef} taRef={taRef}
        Avatar={Avatar} IconPhone={IconPhone} IconClose={IconClose} IconMic={IconMic} IconSend={IconSend}
      />
    );
  }


  // ── LANDING EXPERIENCE ────────────────────────────────
  if (!accessGranted) {
    return (
      <LandingFlow
        landingStage={landingStage}
        navigateLanding={navigateLanding}
        accessCode={accessCode}
        setAccessCode={setAccessCode}
        accessError={accessError}
        submitAccessCode={submitAccessCode}
        authUser={authUser}
        setAuthUser={setAuthUser}
        setAccessGranted={setAccessGranted}
        setLandingStage={setLandingStage}
        handleStripeCheckout={handleStripeCheckout}
        opusAnimCSS={opusAnimCSS}
        css={css}
        onLegalPage={(page) => { setLegalPage(page); window.history.pushState({}, "", `/${page}`); }}
      />
    );
  }


  // ── HOME SCREEN ─────────────────────────────────────────
  return (
    <>
      <style>{css}</style>
      {/* ── FLASH PORTALS — outside overflow:hidden ── */}
      {flashEffect === "connect" && (
        <div key={"c"+flashKey} style={{position:"fixed",inset:0,zIndex:99999,pointerEvents:"none",
          background:"rgba(16,185,129,0.15)",
          boxShadow:"inset 0 0 0 7px rgba(16,185,129,0.95)",
          animation:"flashConnect 0.9s ease-out forwards"}}/>
      )}
      {flashEffect === "disconnect" && (
        <div key={"d"+flashKey} style={{position:"fixed",inset:0,zIndex:99999,pointerEvents:"none",
          background:"rgba(244,63,94,0.15)",
          boxShadow:"inset 0 0 0 7px rgba(244,63,94,0.95)",
          animation:"flashDisconnectBorder 0.9s ease-out forwards"}}/>
      )}
      {/* Registration Modal */}
      {/* Template picker modal — opens from "TEMPLATES" button on AI Agent briefing card */}
      {showTemplatePicker && (() => {
        // Group mergedTemplates dynamically. Categories appear in this order;
        // categories with no templates are skipped.
        const CATEGORY_ORDER = ["My Templates", "Healthcare", "Everyday"];
        const grouped = CATEGORY_ORDER.reduce((acc, cat) => {
          const items = mergedTemplates.filter(t => t.category === cat);
          if (items.length) acc[cat] = items;
          return acc;
        }, {});
        return (
          <div
            onClick={()=>setShowTemplatePicker(false)}
            style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",backdropFilter:"blur(8px)",WebkitBackdropFilter:"blur(8px)",zIndex:9999,display:"flex",alignItems:"flex-start",justifyContent:"center",overflowY:"auto",padding:"20px 16px"}}
          >
            <div
              onClick={e=>e.stopPropagation()}
              style={{background:"var(--s1)",border:"1px solid var(--s3)",borderRadius:20,padding:24,width:"100%",maxWidth:560,display:"flex",flexDirection:"column",gap:16,margin:"auto"}}
            >
              {/* Header */}
              <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:12}}>
                <div>
                  <div style={{fontSize:17,fontWeight:800,color:"var(--tx)"}}>Briefing Templates</div>
                  <div style={{fontSize:11,color:"var(--tx3)",marginTop:2,lineHeight:1.5}}>Pick a template to pre-fill the AI Agent briefing. You can edit anything before placing the call.</div>
                </div>
                <button
                  onClick={()=>setShowTemplatePicker(false)}
                  style={{flexShrink:0,width:28,height:28,borderRadius:8,background:"var(--s2)",border:"1px solid var(--s3)",color:"var(--tx3)",fontSize:14,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}
                  title="Close"
                >
                  ✕
                </button>
              </div>

              {/* Categories */}
              {Object.entries(grouped).map(([cat, items]) => (
                <div key={cat} style={{display:"flex",flexDirection:"column",gap:8}}>
                  <div style={{fontSize:10,fontFamily:"var(--mono)",fontWeight:700,color:"var(--tx3)",letterSpacing:"0.1em"}}>{cat.toUpperCase()}</div>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(160px, 1fr))",gap:8}}>
                    {items.map(tpl => (
                      <button
                        key={tpl.id}
                        onClick={()=>applyTemplate(tpl)}
                        style={{display:"flex",flexDirection:"column",alignItems:"flex-start",gap:6,padding:"12px 12px",borderRadius:12,background:"var(--s2)",border:"1px solid var(--s3)",cursor:"pointer",textAlign:"left",transition:"all .15s",minHeight:80}}
                        onMouseEnter={e=>{e.currentTarget.style.borderColor="var(--ac)";e.currentTarget.style.background="var(--ac3)";}}
                        onMouseLeave={e=>{e.currentTarget.style.borderColor="var(--s3)";e.currentTarget.style.background="var(--s2)";}}
                      >
                        <div style={{fontSize:22,lineHeight:1}}>{tpl.icon}</div>
                        <div style={{fontSize:12,fontWeight:700,color:"var(--tx)",lineHeight:1.2}}>{tpl.name}</div>
                        <div style={{fontSize:10,color:"var(--tx3)",lineHeight:1.4,fontFamily:"var(--mono)",overflow:"hidden",textOverflow:"ellipsis",display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical"}}>{tpl.goal}</div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}

              {/* Footer hint */}
              <div style={{fontSize:10,color:"var(--tx3)",fontFamily:"var(--mono)",textAlign:"center",borderTop:"1px solid var(--s3)",paddingTop:12,lineHeight:1.5}}>
                Saved templates · <span style={{color:"var(--ac2)"}}>SOON</span> — your own templates will appear here
              </div>
            </div>
          </div>
        );
      })()}

      {/* Language picker modal — opens from the language pill above the call button */}
      {showLanguagePicker && (
        <div
          onClick={()=>setShowLanguagePicker(false)}
          style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",backdropFilter:"blur(8px)",WebkitBackdropFilter:"blur(8px)",zIndex:9999,display:"flex",alignItems:"flex-start",justifyContent:"center",overflowY:"auto",padding:"20px 16px"}}
        >
          <div
            onClick={e=>e.stopPropagation()}
            style={{background:"var(--s1)",border:"1px solid var(--s3)",borderRadius:20,padding:24,width:"100%",maxWidth:480,display:"flex",flexDirection:"column",gap:16,margin:"auto"}}
          >
            {/* Header */}
            <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:12}}>
              <div>
                <div style={{fontSize:17,fontWeight:800,color:"var(--tx)",display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:18}}>🌐</span> Translator Language
                </div>
                <div style={{fontSize:11,color:"var(--tx3)",marginTop:4,lineHeight:1.5}}>
                  You type English → AI translates &amp; speaks the chosen language.<br/>
                  They speak the chosen language → AI translates to English for you.
                </div>
              </div>
              <button
                onClick={()=>setShowLanguagePicker(false)}
                style={{flexShrink:0,width:28,height:28,borderRadius:8,background:"var(--s2)",border:"1px solid var(--s3)",color:"var(--tx3)",fontSize:14,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}
                title="Close"
              >
                ✕
              </button>
            </div>

            {/* Language grid */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(110px, 1fr))",gap:8}}>
              {LANGUAGES.map(lang => {
                const sel = targetLanguage.code === lang.code;
                return (
                  <button
                    key={lang.code}
                    onClick={()=>{ saveTargetLanguage(lang); setShowLanguagePicker(false); }}
                    style={{
                      display:"flex",flexDirection:"column",alignItems:"center",gap:6,
                      padding:"14px 8px",borderRadius:12,
                      background:sel?"rgba(139,92,246,0.15)":"var(--s2)",
                      border:`${sel?"2px":"1px"} solid ${sel?"#8b5cf6":"var(--s3)"}`,
                      cursor:"pointer",transition:"all .15s",
                    }}
                  >
                    <span style={{fontSize:28,lineHeight:1}}>{lang.flag}</span>
                    <span style={{fontSize:12,fontWeight:700,color:sel?"#a78bfa":"var(--tx)",lineHeight:1.2}}>{lang.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {showReg && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",zIndex:9999,display:"flex",alignItems:"flex-start",justifyContent:"center",overflowY:"auto",padding:"20px 16px"}}>
          <div style={{background:"var(--s1)",border:"1px solid var(--s3)",borderRadius:20,padding:24,width:"100%",maxWidth:420,display:"flex",flexDirection:"column",gap:12,margin:"auto"}}>

            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <div>
                <div style={{fontSize:17,fontWeight:800,color:"var(--tx)"}}>Create Account</div>
                <div style={{fontSize:11,color:"var(--tx3)",marginTop:2}}>Required for FCC TRS service</div>
              </div>
              <button onClick={()=>setShowReg(false)} style={{color:"var(--tx3)",background:"none",border:"none",fontSize:13}}>
                {user ? "Cancel" : "Skip for now"}
              </button>
            </div>

            {regError && <div style={{background:"rgba(244,63,94,0.1)",border:"1px solid var(--err)",borderRadius:10,padding:"8px 12px",fontSize:12,color:"var(--err)"}}>{regError}</div>}

            {/* Basic info */}
            {[["FULL NAME","full_name","text","Ethan Kramer"],["EMAIL","email","email","ethan@example.com"],["PHONE (optional)","phone","tel","(240) 555-1234"]].map(([label,field,type,ph])=>(
              <div key={field}>
                <div style={{fontSize:9,fontFamily:"var(--mono)",fontWeight:700,color:"var(--tx3)",marginBottom:3}}>{label}</div>
                <input type={type} value={regForm[field]} onChange={e=>setRegForm(f=>({...f,[field]:e.target.value}))}
                  placeholder={ph} style={{width:"100%",fontSize:14,color:"var(--tx)",background:"var(--s2)",border:"1px solid var(--s3)",borderRadius:9,padding:"9px 12px",caretColor:"var(--ac)"}}/>
              </div>
            ))}

            {/* E911 address */}
            <div style={{background:"var(--s2)",border:"1px solid var(--ac)",borderRadius:12,padding:"12px",display:"flex",flexDirection:"column",gap:8}}>
              <div style={{fontSize:9,fontFamily:"var(--mono)",fontWeight:700,color:"var(--ac2)"}}>E911 ADDRESS — REQUIRED FOR EMERGENCY ROUTING</div>
              {[["Street Address","address_line1","123 Main St"],["Apt / Suite (optional)","address_line2","Apt 4B"]].map(([label,field,ph])=>(
                <div key={field}>
                  <div style={{fontSize:9,color:"var(--tx3)",marginBottom:3}}>{label}</div>
                  <input value={regForm[field]} onChange={e=>setRegForm(f=>({...f,[field]:e.target.value}))}
                    placeholder={ph} style={{width:"100%",fontSize:13,color:"var(--tx)",background:"var(--s1)",border:"1px solid var(--s3)",borderRadius:8,padding:"7px 10px",caretColor:"var(--ac)"}}/>
                </div>
              ))}
              <div style={{display:"flex",gap:6}}>
                <div style={{flex:2}}>
                  <div style={{fontSize:9,color:"var(--tx3)",marginBottom:3}}>City</div>
                  <input value={regForm.city} onChange={e=>setRegForm(f=>({...f,city:e.target.value}))} placeholder="Frederick"
                    style={{width:"100%",fontSize:13,color:"var(--tx)",background:"var(--s1)",border:"1px solid var(--s3)",borderRadius:8,padding:"7px 10px",caretColor:"var(--ac)"}}/>
                </div>
                <div style={{flex:1}}>
                  <div style={{fontSize:9,color:"var(--tx3)",marginBottom:3}}>State</div>
                  <input value={regForm.state} onChange={e=>setRegForm(f=>({...f,state:e.target.value.toUpperCase()}))} placeholder="MD" maxLength={2}
                    style={{width:"100%",fontSize:13,color:"var(--tx)",background:"var(--s1)",border:"1px solid var(--s3)",borderRadius:8,padding:"7px 10px",caretColor:"var(--ac)"}}/>
                </div>
                <div style={{flex:1}}>
                  <div style={{fontSize:9,color:"var(--tx3)",marginBottom:3}}>ZIP</div>
                  <input value={regForm.zip} onChange={e=>setRegForm(f=>({...f,zip:e.target.value}))} placeholder="21701" maxLength={5}
                    style={{width:"100%",fontSize:13,color:"var(--tx)",background:"var(--s1)",border:"1px solid var(--s3)",borderRadius:8,padding:"7px 10px",caretColor:"var(--ac)"}}/>
                </div>
              </div>
              <div>
                <div style={{fontSize:9,color:"var(--tx3)",marginBottom:3}}>Location Type</div>
                <div style={{display:"flex",gap:6}}>
                  {["home","work","other"].map(t=>(
                    <button key={t} onClick={()=>setRegForm(f=>({...f,location_type:t}))}
                      style={{flex:1,padding:"6px",borderRadius:7,fontSize:11,fontWeight:600,
                        background:regForm.location_type===t?"var(--ac3)":"var(--s1)",
                        border:regForm.location_type===t?"1.5px solid var(--ac)":"1px solid var(--s3)",
                        color:regForm.location_type===t?"var(--ac2)":"var(--tx3)"}}>
                      {t.charAt(0).toUpperCase()+t.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Legal */}
            {[
              ["terms_accepted","I agree to the Terms of Service"],
              ["privacy_accepted","I agree to the Privacy Policy and understand my call content is never stored"],
            ].map(([field,label])=>(
              <button key={field} onClick={()=>setRegForm(f=>({...f,[field]:!f[field]}))}
                style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",borderRadius:10,
                  background:regForm[field]?"var(--ac3)":"var(--s2)",
                  border:regForm[field]?"1.5px solid var(--ac)":"1px solid var(--s3)",textAlign:"left"}}>
                <div style={{width:16,height:16,borderRadius:4,border:"2px solid",borderColor:regForm[field]?"var(--ac)":"var(--tx3)",
                  background:regForm[field]?"var(--ac)":"transparent",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"}}>
                  {regForm[field] && <span style={{color:"#fff",fontSize:10,fontWeight:700}}>✓</span>}
                </div>
                <span style={{fontSize:12,color:"var(--tx)",lineHeight:1.4}}>{label}</span>
              </button>
            ))}

            <button onClick={submitRegistration} disabled={regLoading}
              style={{padding:"14px",borderRadius:12,background:"var(--ac)",color:"#fff",fontSize:15,fontWeight:700,border:"none",opacity:regLoading?0.7:1}}>
              {regLoading?"Creating Account…":"Create Account"}
            </button>
          </div>
        </div>
      )}

      {showTranscriptPrompt && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:999,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
          <div style={{background:"var(--s1)",border:"1px solid var(--s3)",borderRadius:20,padding:24,width:"100%",maxWidth:360,display:"flex",flexDirection:"column",gap:16}}>
            <div style={{fontSize:16,fontWeight:700,color:"var(--tx)"}}>Save transcript?</div>
            <div style={{fontSize:13,color:"var(--tx3)",lineHeight:1.5}}>Would you like to save a copy of this call transcript?</div>
            {!transcriptEmail && (
              <div>
                <div style={{fontSize:11,color:"var(--tx3)",marginBottom:6,fontFamily:"var(--mono)"}}>EMAIL ADDRESS (optional)</div>
                <input
                  placeholder="you@email.com"
                  type="email"
                  defaultValue=""
                  id="modal-email-input"
                  style={{width:"100%",fontSize:14,color:"var(--tx)",background:"var(--s2)",border:"1px solid var(--s3)",borderRadius:8,padding:"8px 12px",caretColor:"var(--ac)"}}/>
              </div>
            )}
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>{downloadTranscript();setShowTranscriptPrompt(false);}}
                  style={{flex:1,padding:"12px",borderRadius:12,background:"var(--ac)",color:"#fff",fontSize:14,fontWeight:700,border:"none"}}>
                  Download .txt
                </button>
                <button onClick={()=>{downloadTranscriptPDF();setShowTranscriptPrompt(false);}}
                  style={{flex:1,padding:"12px",borderRadius:12,background:"var(--s2)",color:"var(--tx)",fontSize:14,fontWeight:600,border:"1px solid var(--s3)"}}>
                  Download .pdf
                </button>
              </div>
              <button onClick={async ()=>{
                  const email = transcriptEmail || document.getElementById("modal-email-input")?.value;
                  if(email) { saveTranscriptEmail(email); }
                  setShowTranscriptPrompt(false);
                  if(email) await emailTranscript(email);
                }}
                style={{padding:"12px",borderRadius:12,background:"var(--s2)",color:"var(--tx)",fontSize:14,fontWeight:600,border:"1px solid var(--s3)"}}>
                {transcriptEmail ? `Email to ${transcriptEmail}` : "Email transcript"}
              </button>
              <button onClick={()=>setShowTranscriptPrompt(false)}
                style={{padding:"10px",borderRadius:12,background:"transparent",color:"var(--tx3)",fontSize:13,border:"none"}}>
                No thanks
              </button>
            </div>
          </div>
        </div>
      )}
      <div style={{height:"100dvh",display:"flex",flexDirection:"column",background:"var(--bg)",overflow:"hidden",paddingTop:"env(safe-area-inset-top)",paddingBottom:"env(safe-area-inset-bottom)",position:"relative"}}>

        {/* ── CALL FLASH EFFECTS ── */}
        {flashEffect === "connect" && (
          <div key={"c"+flashKey} style={{position:"fixed",inset:0,zIndex:9997,pointerEvents:"none",
            background:"rgba(16,185,129,0.18)",
            boxShadow:"inset 0 0 0 6px rgba(16,185,129,0.9)",
            animation:"flashConnect 0.9s ease-out forwards"}}/>
        )}
        {flashEffect === "disconnect" && (
          <div key={"d"+flashKey} style={{position:"fixed",inset:0,zIndex:9997,pointerEvents:"none",
            background:"rgba(244,63,94,0.18)",
            boxShadow:"inset 0 0 0 6px rgba(244,63,94,0.9)",
            animation:"flashDisconnectBorder 0.9s ease-out forwards"}}/>
        )}

        {/* Header */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 20px 8px",flexShrink:0}}>
          <div style={{display:"flex",alignItems:"baseline",gap:2}}>
            <span style={{fontSize:18,fontWeight:800,letterSpacing:"-0.03em"}}>my</span>
            <span style={{fontSize:18,fontWeight:800,letterSpacing:"-0.03em",color:"var(--ac2)"}}>relay</span>
            <span style={{fontSize:10,color:"var(--tx3)",fontFamily:"var(--mono)",marginLeft:2,fontWeight:400}}>.ai</span>
          </div>
          <div style={{display:"flex",gap:6,alignItems:"center"}}>
            <button onClick={()=>setDark(d=>!d)} style={{width:32,height:32,borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",background:"var(--s2)",border:"1px solid var(--s3)",transition:"all .15s"}}>
              {dark ? <IconSun size={14} color="var(--tx3)"/> : <IconMoon size={14} color="var(--tx3)"/>}
            </button>
            {authUser ? (
              <div style={{position:"relative"}}>
                <button
                  onClick={()=>setShowUserMenu(m=>!m)}
                  style={{display:"flex",alignItems:"center",gap:5,padding:"4px 10px",borderRadius:8,background:showUserMenu?"var(--ac3)":"var(--s2)",border:`1px solid ${showUserMenu?"var(--ac)":"var(--s3)"}`,fontSize:10,fontFamily:"var(--mono)",color:showUserMenu?"var(--ac2)":"var(--tx)",letterSpacing:"0.06em"}}
                  title={authUser.email}
                >
                  <IconUser size={12} color={showUserMenu?"var(--ac2)":"var(--tx3)"}/>
                  {authUser.email?.split('@')[0].slice(0,10)}
                </button>
                {showUserMenu && (
                  <>
                    {/* Click-outside overlay */}
                    <div
                      onClick={()=>setShowUserMenu(false)}
                      style={{position:"fixed",inset:0,zIndex:998}}
                    />
                    <div style={{position:"absolute",top:"calc(100% + 4px)",right:0,minWidth:200,background:"var(--s1)",border:"1px solid var(--s3)",borderRadius:10,boxShadow:"0 8px 24px rgba(0,0,0,0.25)",overflow:"hidden",zIndex:999}}>
                      <div style={{padding:"10px 12px",borderBottom:"1px solid var(--s3)",fontSize:11,color:"var(--tx3)",fontFamily:"var(--mono)",wordBreak:"break-all"}}>
                        {authUser.email}
                      </div>
                      <button
                        disabled
                        style={{width:"100%",textAlign:"left",padding:"10px 12px",background:"transparent",border:"none",fontSize:12,color:"var(--tx3)",cursor:"not-allowed",opacity:0.6}}
                        title="Account settings — coming soon"
                      >
                        Account settings · <span style={{fontSize:9,fontFamily:"var(--mono)"}}>soon</span>
                      </button>
                      <button
                        onClick={()=>{setShowUserMenu(false); signOut();}}
                        style={{width:"100%",textAlign:"left",padding:"10px 12px",background:"transparent",border:"none",borderTop:"1px solid var(--s3)",fontSize:12,color:"var(--err)",cursor:"pointer",fontWeight:600}}
                      >
                        Sign out
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <button onClick={()=>setShowReg(true)} style={{width:32,height:32,borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(244,63,94,0.15)",border:"1px solid var(--err)",transition:"all .15s"}} title="Register to use MyRelay">
                <IconUser size={14} color="var(--err)"/>
              </button>
            )}
            <button onClick={()=>{setTab(t=>t==="history"?"dial":"history"); if(tab!=="history") fetchHistory();}} style={{width:32,height:32,borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",background:tab==="history"?"var(--ac3)":"var(--s2)",border:`1px solid ${tab==="history"?"var(--ac)":"var(--s3)"}`,transition:"all .15s"}} title="Call history">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={tab==="history"?"var(--ac2)":"var(--tx3)"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            </button>
            <button onClick={()=>setTab(t=>t==="settings"?"dial":"settings")} style={{width:32,height:32,borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",background:tab==="settings"?"var(--ac3)":"var(--s2)",border:`1px solid ${tab==="settings"?"var(--ac)":"var(--s3)"}`,transition:"all .15s"}}>
              <IconSettings size={14} color={tab==="settings"?"var(--ac2)":"var(--tx3)"}/>
            </button>
          </div>
        </div>

        {/* Tab content */}
        {tab === "settings" ? (
          <div className="tab-content" style={{flex:1,overflowY:"auto",padding:"0 16px 32px",display:"flex",flexDirection:"column",gap:16}}>

            {/* Sub-tab nav — sticky horizontal pill row */}
            <div style={{position:"sticky",top:0,zIndex:5,background:"var(--bg)",paddingTop:4,paddingBottom:8,marginLeft:-16,marginRight:-16,paddingLeft:16,paddingRight:16}}>
              <div style={{display:"flex",gap:6,overflowX:"auto",scrollbarWidth:"none",msOverflowStyle:"none"}}>
                {[
                  ["account","Account"],
                  ["calling","Calling"],
                  ["templates","Templates & Shortcuts"],
                  ["billing","Billing"],
                  ["compliance","Compliance & Privacy"],
                  ["notifications","Notifications"],
                ].map(([key,label])=>{
                  const sel = settingsSubTab===key;
                  return (
                    <button key={key} onClick={()=>setSettingsSubTab(key)}
                      style={{
                        flexShrink:0,
                        padding:"7px 13px",
                        borderRadius:999,
                        fontSize:11,
                        fontFamily:"var(--mono)",
                        fontWeight:sel?700:500,
                        letterSpacing:"0.04em",
                        background:sel?"var(--ac3)":"var(--s2)",
                        border:`1px solid ${sel?"var(--ac)":"var(--s3)"}`,
                        color:sel?"var(--ac2)":"var(--tx3)",
                        cursor:"pointer",
                        transition:"all .15s",
                        whiteSpace:"nowrap",
                      }}>
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ─── ACCOUNT ─── */}
            {settingsSubTab === "account" && (<>

            {/* Your Name */}
            <div style={{background:"var(--s1)",border:"1px solid var(--s3)",borderRadius:16,padding:"16px"}}>
              <div style={{fontSize:10,fontFamily:"var(--mono)",fontWeight:700,color:"var(--tx3)",letterSpacing:"0.08em",marginBottom:8}}>YOUR NAME</div>
              <div style={{fontSize:11,color:"var(--tx3)",marginBottom:10,lineHeight:1.5}}>Used in intro messages and AI Agent calls.</div>
              <input value={userName} onChange={e=>saveUserName(e.target.value)}
                placeholder="Enter your name"
                style={{width:"100%",fontSize:14,color:"var(--tx)",background:"var(--s2)",border:"1px solid var(--s3)",borderRadius:10,padding:"10px 12px",caretColor:"var(--ac)"}}/>
              {!userName && (
                <div style={{fontSize:10,color:"#f59e0b",fontFamily:"var(--mono)",marginTop:6}}>⚠ Set your name so callers know who's calling</div>
              )}
            </div>

            </>)}

            {/* ─── CALLING ─── */}
            {settingsSubTab === "calling" && (<>

            {/* Intro Message */}
            <div style={{background:"var(--s1)",border:"1px solid var(--s3)",borderRadius:16,padding:"16px"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                <div style={{fontSize:10,fontFamily:"var(--mono)",fontWeight:700,color:"var(--tx3)",letterSpacing:"0.08em"}}>INTRO MESSAGE</div>
                <button onClick={()=>saveIntroEnabled(!introEnabled)} style={{width:40,height:22,borderRadius:11,background:introEnabled?"var(--ac)":"var(--s3)",border:"none",position:"relative",transition:"all .2s",flexShrink:0}}>
                  <div style={{width:16,height:16,borderRadius:"50%",background:"#fff",position:"absolute",top:3,left:introEnabled?20:3,transition:"left .2s"}}/>
                </button>
              </div>
              <div style={{fontSize:11,color:"var(--tx3)",marginBottom:10,lineHeight:1.5}}>Spoken automatically when a call connects.</div>
              <textarea value={introMsg} onChange={e=>saveIntro(e.target.value)} rows={3} disabled={!introEnabled}
                style={{width:"100%",fontSize:14,color:introEnabled?"var(--tx)":"var(--tx3)",background:"var(--s2)",border:"1px solid var(--s3)",borderRadius:10,padding:"10px 12px",resize:"none",fontFamily:"inherit",lineHeight:1.5,caretColor:"var(--ac)",opacity:introEnabled?1:0.5}}/>
              <div style={{display:"flex",justifyContent:"space-between",marginTop:6}}>
                <span style={{fontSize:10,color:"var(--tx3)",fontFamily:"var(--mono)"}}>{introMsg.length} chars</span>
                <button onClick={()=>saveIntro(DEFAULT_INTRO)} style={{fontSize:10,color:"var(--tx3)",fontFamily:"var(--mono)",textDecoration:"underline"}}>Reset</button>
              </div>
            </div>

            </>)}

            {/* ─── TEMPLATES &amp; SHORTCUTS ─── */}
            {settingsSubTab === "templates" && (<>

            {/* Briefing Templates — expandable editable cards */}
            <div style={{background:"var(--s1)",border:"1px solid var(--s3)",borderRadius:16,padding:"16px"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                <div style={{fontSize:10,fontFamily:"var(--mono)",fontWeight:700,color:"var(--tx3)",letterSpacing:"0.08em"}}>BRIEFING TEMPLATES</div>
                <span style={{fontSize:9,fontFamily:"var(--mono)",color:"var(--tx3)"}}>{mergedTemplates.length} total</span>
              </div>
              <div style={{fontSize:11,color:"var(--tx3)",marginBottom:12,lineHeight:1.5}}>
                {authUser
                  ? "Tap a template to personalize it with your info (name, DOB, account numbers). Your edits save to your account."
                  : "Sign in to personalize templates with your info. Until then, you'll use the generic defaults."}
              </div>

              {/* Custom templates section (only if any exist) */}
              {mergedTemplates.some(t => t.isCustom) && (
                <>
                  <div style={{fontSize:9,fontFamily:"var(--mono)",color:"var(--tx3)",letterSpacing:"0.1em",marginTop:4,marginBottom:6}}>MY TEMPLATES</div>
                  <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:10}}>
                    {mergedTemplates.filter(t => t.isCustom).map(tpl => (
                      <TemplateEditCard
                        key={tpl.id}
                        tpl={tpl}
                        expanded={expandedTemplateId === tpl.id}
                        editBuffer={templateEditBuffer}
                        setEditBuffer={setTemplateEditBuffer}
                        saving={templateSaving}
                        onToggle={()=>expandTemplate(tpl)}
                        onSave={()=>persistTemplateEdit(tpl)}
                        onReset={null}
                        onDelete={()=>removeCustomTemplate(tpl)}
                        authUser={authUser}
                      />
                    ))}
                  </div>
                </>
              )}

              {/* Built-in categories */}
              {["Healthcare", "Everyday"].map(cat => {
                const items = mergedTemplates.filter(t => t.category === cat);
                if (!items.length) return null;
                return (
                  <div key={cat}>
                    <div style={{fontSize:9,fontFamily:"var(--mono)",color:"var(--tx3)",letterSpacing:"0.1em",marginTop:4,marginBottom:6}}>{cat.toUpperCase()}</div>
                    <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:10}}>
                      {items.map(tpl => (
                        <TemplateEditCard
                          key={tpl.id}
                          tpl={tpl}
                          expanded={expandedTemplateId === tpl.id}
                          editBuffer={templateEditBuffer}
                          setEditBuffer={setTemplateEditBuffer}
                          saving={templateSaving}
                          onToggle={()=>expandTemplate(tpl)}
                          onSave={()=>persistTemplateEdit(tpl)}
                          onReset={tpl.userRowId ? ()=>resetGlobalTemplate(tpl) : null}
                          onDelete={null}
                          authUser={authUser}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}

              <div style={{marginTop:8,padding:"10px 12px",background:"var(--s2)",border:"1px dashed var(--s3)",borderRadius:9,fontSize:11,color:"var(--tx3)",lineHeight:1.5}}>
                <span style={{fontFamily:"var(--mono)",fontWeight:700,color:"var(--ac2)"}}>TIP</span> — create new templates by filling out the AI Agent briefing on the dial screen and tapping <span style={{fontFamily:"var(--mono)",fontWeight:700}}>💾 SAVE</span>
              </div>
            </div>

            {/* Shortcuts */}
            <div style={{background:"var(--s1)",border:"1px solid var(--s3)",borderRadius:16,padding:"16px"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
                <div style={{fontSize:10,fontFamily:"var(--mono)",fontWeight:700,color:"var(--tx3)",letterSpacing:"0.08em"}}>SHORTCUTS</div>
                <button onClick={()=>saveShortcutsEnabled(!shortcutsEnabled)} style={{width:40,height:22,borderRadius:11,background:shortcutsEnabled?"var(--ac)":"var(--s3)",border:"none",position:"relative",transition:"all .2s",flexShrink:0}}>
                  <div style={{width:16,height:16,borderRadius:"50%",background:"#fff",position:"absolute",top:3,left:shortcutsEnabled?20:3,transition:"left .2s"}}/>
                </button>
              </div>
              <div style={{fontSize:11,color:"var(--tx3)",marginBottom:12,lineHeight:1.5}}>Type a shortcut while on a call to expand it instantly. e.g. <span style={{fontFamily:"var(--mono)",color:"var(--ac2)"}}>/dob</span> expands to your date of birth.</div>

              {/* Shortcut list */}
              <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:12}}>
                {shortcuts.map(s=>(
                  <div key={s.key} style={{display:"flex",gap:8,alignItems:"center"}}>
                    <div style={{fontFamily:"var(--mono)",fontSize:12,fontWeight:700,color:"var(--ac2)",background:"var(--ac3)",border:"1px solid var(--ac)",borderRadius:6,padding:"4px 8px",flexShrink:0,minWidth:60,textAlign:"center"}}>{s.key}</div>
                    <input value={s.value} onChange={e=>updateShortcutVal(s.key,e.target.value)}
                      placeholder="Expansion text..."
                      style={{flex:1,fontSize:13,color:"var(--tx)",background:"var(--s2)",border:"1px solid var(--s3)",borderRadius:8,padding:"6px 10px",caretColor:"var(--ac)"}}/>
                    <button onClick={()=>deleteShortcut(s.key)} style={{width:28,height:28,borderRadius:7,background:"rgba(244,63,94,0.1)",border:"1px solid rgba(244,63,94,0.3)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                      <IconTrash size={12} color="#f43f5e"/>
                    </button>
                  </div>
                ))}
              </div>

              {/* Add new shortcut */}
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                <input value={newKey} onChange={e=>setNewKey(e.target.value.startsWith("/")?e.target.value:"/"+e.target.value)}
                  placeholder="/shortcut"
                  style={{width:90,fontSize:13,fontFamily:"var(--mono)",color:"var(--ac2)",background:"var(--s2)",border:"1px solid var(--s3)",borderRadius:8,padding:"7px 10px",caretColor:"var(--ac)",flexShrink:0}}/>
                <input value={newVal} onChange={e=>setNewVal(e.target.value)}
                  placeholder="Expansion text..."
                  style={{flex:1,fontSize:13,color:"var(--tx)",background:"var(--s2)",border:"1px solid var(--s3)",borderRadius:8,padding:"7px 10px",caretColor:"var(--ac)"}}/>
                <button onClick={addShortcut} disabled={!newKey||!newVal.trim()||newKey==="/"} style={{width:32,height:32,borderRadius:8,background:"var(--ac)",border:"none",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,opacity:(!newKey||!newVal.trim()||newKey==="/")?0.4:1}}>
                  <IconPlus size={16} color="#fff"/>
                </button>
              </div>
            </div>

            </>)}

            {/* ─── BILLING ─── */}
            {settingsSubTab === "billing" && (<>
            <div style={{background:"var(--s1)",border:"1px dashed var(--s3)",borderRadius:16,padding:"16px"}}>
              <div style={{fontSize:10,fontFamily:"var(--mono)",fontWeight:700,color:"var(--tx3)",letterSpacing:"0.08em",marginBottom:8}}>BILLING <span style={{color:"var(--ac2)",marginLeft:6}}>SOON</span></div>
              <div style={{fontSize:11,color:"var(--tx3)",lineHeight:1.5}}>Plan, usage, and Stripe billing portal will live here.</div>
            </div>
            </>)}

            {/* ─── COMPLIANCE &amp; PRIVACY ─── */}
            {settingsSubTab === "compliance" && (<>

            {/* Report a Complaint — FCC §64.604(a)(4) */}
            <div style={{background:"var(--s1)",border:"1px solid var(--s3)",borderRadius:16,padding:"16px"}}>
              <div style={{fontSize:10,fontFamily:"var(--mono)",fontWeight:700,color:"var(--tx3)",letterSpacing:"0.08em",marginBottom:4}}>REPORT AN ISSUE</div>
              <div style={{fontSize:11,color:"var(--tx3)",marginBottom:12,lineHeight:1.5}}>FCC requires us to log and respond to complaints within 1 business day.</div>

              {complaintSent ? (
                <div style={{background:"rgba(16,185,129,0.1)",border:"1px solid #10b981",borderRadius:10,padding:"12px",textAlign:"center"}}>
                  <div style={{fontSize:14,color:"#10b981",fontWeight:700}}>Complaint Filed</div>
                  <div style={{fontSize:12,color:"var(--tx3)",marginTop:4}}>We'll respond to {user?.email || "you"} within 1 business day.</div>
                  <button onClick={()=>setComplaintSent(false)} style={{marginTop:8,fontSize:11,color:"var(--tx3)",textDecoration:"underline",background:"none",border:"none"}}>File another</button>
                </div>
              ) : (
                <>
                  <div style={{marginBottom:8}}>
                    <div style={{fontSize:9,fontFamily:"var(--mono)",color:"var(--tx3)",marginBottom:4}}>CATEGORY</div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:4}}>
                      {[["audio_quality","Audio Quality"],["connection","Connection"],["transcription","Transcription"],["billing","Billing"],["accessibility","Accessibility"],["other","Other"]].map(([val,label])=>(
                        <button key={val} onClick={()=>setComplaint(c=>({...c,category:val}))}
                          style={{padding:"7px 8px",borderRadius:8,fontSize:11,fontWeight:600,textAlign:"left",
                            background:complaint.category===val?"var(--ac3)":"var(--s2)",
                            border:complaint.category===val?"1.5px solid var(--ac)":"1px solid var(--s3)",
                            color:complaint.category===val?"var(--ac2)":"var(--tx3)"}}>
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div style={{marginBottom:10}}>
                    <div style={{fontSize:9,fontFamily:"var(--mono)",color:"var(--tx3)",marginBottom:4}}>DESCRIPTION</div>
                    <textarea value={complaint.description} onChange={e=>setComplaint(c=>({...c,description:e.target.value}))}
                      placeholder="Describe the issue in detail..."
                      rows={3}
                      style={{width:"100%",fontSize:13,color:"var(--tx)",background:"var(--s2)",border:"1px solid var(--s3)",borderRadius:9,padding:"9px 12px",resize:"none",fontFamily:"inherit",caretColor:"var(--ac)"}}/>
                  </div>
                  <button onClick={submitComplaint} disabled={!complaint.description.trim()||complaintLoading}
                    style={{width:"100%",padding:"11px",borderRadius:10,background:"var(--ac)",color:"#fff",fontSize:13,fontWeight:700,border:"none",opacity:(!complaint.description.trim()||complaintLoading)?0.5:1}}>
                    {complaintLoading?"Submitting…":"Submit Complaint"}
                  </button>
                  {!user && <div style={{fontSize:10,color:"var(--tx3)",marginTop:6,textAlign:"center"}}>Register to attach your account to this complaint.</div>}
                </>
              )}
            </div>

            {/* Transcript Settings */}
            <div style={{background:"var(--s1)",border:"1px solid var(--s3)",borderRadius:16,padding:"16px"}}>
              <div style={{fontSize:10,fontFamily:"var(--mono)",fontWeight:700,color:"var(--tx3)",letterSpacing:"0.08em",marginBottom:8}}>TRANSCRIPT AFTER CALL</div>
              <div style={{fontSize:11,color:"var(--tx3)",marginBottom:12,lineHeight:1.5}}>FCC-compliant: transcript is sent directly to you, never stored on our servers.</div>
              <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:12}}>
                {[["off","Off — no transcript"],["ask","Ask me after each call"],["always","Always email automatically"]].map(([v,label])=>(
                  <button key={v} onClick={()=>saveTranscriptMode(v)}
                    style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",borderRadius:10,
                      background:transcriptMode===v?"var(--ac3)":"var(--s2)",
                      border:`${transcriptMode===v?"2px":"1px"} solid ${transcriptMode===v?"var(--ac)":"var(--s3)"}`,
                      textAlign:"left"}}>
                    <div style={{width:14,height:14,borderRadius:"50%",border:`2px solid ${transcriptMode===v?"var(--ac)":"var(--tx3)"}`,background:transcriptMode===v?"var(--ac)":"transparent",flexShrink:0}}/>
                    <span style={{fontSize:13,color:transcriptMode===v?"var(--ac2)":"var(--tx)",fontWeight:transcriptMode===v?600:400}}>{label}</span>
                  </button>
                ))}
              </div>
              {transcriptMode !== "off" && (
                <div>
                  <div style={{fontSize:10,fontFamily:"var(--mono)",color:"var(--tx3)",marginBottom:6}}>EMAIL ADDRESS (optional — for email delivery)</div>
                  <input value={transcriptEmail} onChange={e=>saveTranscriptEmail(e.target.value)}
                    placeholder="you@email.com"
                    type="email"
                    style={{width:"100%",fontSize:14,color:"var(--tx)",background:"var(--s2)",border:"1px solid var(--s3)",borderRadius:8,padding:"8px 12px",caretColor:"var(--ac)"}}/>
                  <div style={{fontSize:10,color:"var(--tx3)",marginTop:4,lineHeight:1.4}}>Transcript will be emailed here after each call. Beautifully formatted with full conversation.</div>
                </div>
              )}
            </div>

            </>)}

            {/* ─── NOTIFICATIONS ─── */}
            {settingsSubTab === "notifications" && (<>
            <div style={{background:"var(--s1)",border:"1px dashed var(--s3)",borderRadius:16,padding:"16px"}}>
              <div style={{fontSize:10,fontFamily:"var(--mono)",fontWeight:700,color:"var(--tx3)",letterSpacing:"0.08em",marginBottom:8}}>NOTIFICATIONS <span style={{color:"var(--ac2)",marginLeft:6}}>SOON</span></div>
              <div style={{fontSize:11,color:"var(--tx3)",lineHeight:1.5}}>Email and push notification preferences will live here.</div>
            </div>
            </>)}

          </div>
        ) : tab === "history" ? (
        <div className="tab-content" key="history">
          <CallHistory calls={callHistory} loading={historyLoading}/>
        </div>
        ) : (
        <div className="tab-content" style={{flex:1,display:"flex",flexDirection:"column",padding:"0 16px 140px",gap:8,overflowY:"auto",overflowX:"hidden"}}>

          {/* Usage bar */}
          {usage && (
            <div style={{...S.card,padding:"12px 16px",flexShrink:0}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <span style={{fontSize:10,fontFamily:"var(--mono)",color:"var(--tx3)",letterSpacing:"0.08em",textTransform:"uppercase"}}>{usage.plan} plan</span>
                  {usage.overage > 0 && (
                    <span style={{fontSize:9,fontFamily:"var(--mono)",color:"#f59e0b",background:"rgba(245,158,11,0.1)",border:"1px solid rgba(245,158,11,0.25)",borderRadius:4,padding:"1px 6px",letterSpacing:"0.06em"}}>PAYG: ${usage.overageCost}</span>
                  )}
                </div>
                <span style={{fontSize:11,fontFamily:"var(--mono)",fontWeight:600,color:usage.remaining<=5?"var(--err)":usage.remaining<=15?"#f59e0b":"var(--tx)"}}>
                  {usage.minutesUsed}/{usage.limit} min
                </span>
              </div>
              <div style={{height:4,borderRadius:2,background:"var(--s3)",overflow:"hidden"}}>
                <div style={{height:"100%",borderRadius:2,transition:"width 0.5s ease",width:`${Math.min(100,(usage.minutesUsed/usage.limit)*100)}%`,background:usage.remaining<=5?"var(--err)":usage.remaining<=15?"#f59e0b":"var(--ac)"}}/>
              </div>
              {usage.remaining <= 5 && usage.remaining > 0 && (
                <div style={{fontSize:10,color:"#f59e0b",fontFamily:"var(--mono)",marginTop:6}}>⚠ {usage.remaining} minutes remaining this month</div>
              )}
              {usage.remaining === 0 && usage.overage === 0 && (
                <div style={{fontSize:10,color:"var(--err)",fontFamily:"var(--mono)",marginTop:6}}>Minutes exhausted — calls billed at 10¢/min</div>
              )}
            </div>
          )}

          {/* Phone display */}
          <div style={{...S.card,padding:"14px 18px",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0,transition:"all 0.3s",borderColor:phone?"var(--ac)":"var(--s3)",boxShadow:phone?"0 0 0 2px rgba(79,124,255,0.1)":"none"}}>
            <span style={{fontFamily:"var(--mono)",fontSize:22,letterSpacing:"0.03em",fontWeight:600,color:phone?"var(--tx)":"var(--tx3)"}}>
              {phone?fmt(phone):"Enter number"}
            </span>
            {phone && (
              <button onClick={()=>setPhone(p=>p.slice(0,-1))} style={{width:32,height:32,borderRadius:8,background:"var(--s2)",border:"1px solid var(--s3)",display:"flex",alignItems:"center",justifyContent:"center"}}>
                <IconDelete size={14} color="var(--tx3)"/>
              </button>
            )}
          </div>

          {/* Dial pad */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:6,flexShrink:0}}>
            {KEYS.map(k=>(
              <button key={k} onClick={()=>dialKey(k)} className="dial-key"
                style={{padding:"12px 0",borderRadius:12,background:"var(--s1)",border:"1px solid var(--s3)",display:"flex",flexDirection:"column",alignItems:"center",gap:1,transition:"all .1s"}}>
                <span style={{fontSize:18,fontFamily:"var(--mono)",fontWeight:600,color:"var(--tx)",lineHeight:1}}>{k}</span>
                {KEY_LETTERS[k] && <span style={{fontSize:8,fontFamily:"var(--mono)",color:"var(--tx3)",letterSpacing:"0.12em",lineHeight:1}}>{KEY_LETTERS[k]}</span>}
              </button>
            ))}
          </div>

          {/* Voice selector */}
          <div style={{flexShrink:0}}>
            <div style={S.label}>VOICE</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:5}}>
              {VOICES.map(v=>{
                const sel = voice.id===v.id;
                const disabled = !!v.comingSoon;
                return (
                  <button
                    key={v.id}
                    onClick={disabled ? undefined : ()=>setVoice(v)}
                    disabled={disabled}
                    className={sel?"voice-selected":""}
                    style={{display:"flex",alignItems:"center",gap:8,padding:"10px 10px",borderRadius:11,background:disabled?"var(--s1)":(sel?"var(--sel-voice-bg)":"var(--s1)"),border:`${sel?"2.5px":"1px"} solid ${sel?"var(--sel-voice-border)":"var(--s3)"}`,transition:"all .15s",overflow:"hidden",opacity:disabled?0.5:1,cursor:disabled?"not-allowed":"pointer"}}>
                    <div style={{width:28,height:28,borderRadius:"50%",flexShrink:0,background:disabled?"var(--s3)":(sel?v.gradient:"var(--s3)"),display:"flex",alignItems:"center",justifyContent:"center",fontSize:disabled?14:10,fontWeight:800,color:"#fff",transition:"all .15s"}}>
                      {disabled ? "⋯" : v.name[0]}
                    </div>
                    <div style={{textAlign:"left",minWidth:0}}>
                      <div style={{fontSize:12,fontWeight:700,color:sel?"var(--sel-voice-tx)":"var(--tx)",lineHeight:1.2}}>{v.name}</div>
                      <div style={{fontSize:9,color:sel?"var(--sel-voice-tag)":"var(--tx3)",fontFamily:"var(--mono)",lineHeight:1.3,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{v.tag}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Avatar + Mode row */}
          <div style={{display:"flex",gap:8,flexShrink:0,alignItems:"stretch"}}>
            {/* Avatar selector */}
            <div style={{flex:1,display:"flex",flexDirection:"column"}}>
              <div style={S.label}>AVATAR</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:4,flex:1}}>
                {AVATARS.map(a=>{
                  const C = a.Component;
                  const sel = avatarId===a.id;
                  return (
                    <button key={a.id} onClick={()=>setAvatarId(a.id)}
                      style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:3,padding:"8px 4px",borderRadius:10,background:sel?"var(--sel-av-bg)":"var(--s1)",border:`${sel?"2.5px":"1px"} solid ${sel?"var(--sel-av-border)":"var(--s3)"}`,transition:"all .15s"}}>
                      {C ? <C state="idle" size={22}/> : <span style={{fontSize:16,color:sel?"var(--sel-av-tx)":"var(--tx3)",lineHeight:1}}>—</span>}
                      <div style={{fontSize:8,fontWeight:600,color:sel?"var(--sel-av-tx)":"var(--tx3)",fontFamily:"var(--mono)"}}>{a.label}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Mode toggle */}
            <div style={{flex:1,display:"flex",flexDirection:"column"}}>
              <div style={S.label}>MODE</div>
              <div style={{display:"flex",flexDirection:"column",gap:4,flex:1}}>
                {[
                  ["manual","Manual",<IconPen size={13}/>],
                  ["agent","AI Agent",<IconBot size={13}/>],
                ].map(([m,l,icon])=>(
                  <button key={m} onClick={()=>setCallMode(m)}
                    style={{flex:1,padding:"10px 10px",borderRadius:10,fontSize:12,fontWeight:700,display:"flex",alignItems:"center",gap:7,transition:"all .15s",
                      background:callMode===m?"var(--ac3)":"var(--s1)",
                      border:callMode===m?"2px solid var(--ac)":`1px solid var(--s3)`,
                      color:callMode===m?"var(--ac2)":"var(--tx2)"}}>
                    {icon}{l}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Agent briefing */}
          {callMode==="agent" && (
            <div style={{background:"var(--s1)",border:"1.5px solid var(--ac)",borderRadius:14,padding:"14px",flexShrink:0,display:"flex",flexDirection:"column",gap:8}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:6}}>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <IconBot size={12} color="var(--ac2)"/>
                  <span style={{fontSize:10,fontFamily:"var(--mono)",color:"var(--ac2)",letterSpacing:"0.1em",fontWeight:700}}>AI AGENT BRIEFING</span>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  {authUser && (agentWho || agentGoal || agentContext || agentInstructions) && (
                    <button
                      onClick={saveCurrentAsTemplate}
                      style={{display:"flex",alignItems:"center",gap:4,padding:"4px 9px",borderRadius:7,background:"var(--s2)",border:"1px solid var(--s3)",fontSize:10,fontFamily:"var(--mono)",color:"var(--tx3)",letterSpacing:"0.06em",fontWeight:700,cursor:"pointer"}}
                      title="Save these briefing fields as a reusable template"
                    >
                      💾 SAVE
                    </button>
                  )}
                  <button
                    onClick={()=>setShowTemplatePicker(true)}
                    style={{display:"flex",alignItems:"center",gap:5,padding:"4px 10px",borderRadius:7,background:"var(--ac3)",border:"1px solid var(--ac)",fontSize:10,fontFamily:"var(--mono)",color:"var(--ac2)",letterSpacing:"0.06em",fontWeight:700,cursor:"pointer"}}
                    title="Pick a briefing template"
                  >
                    📋 TEMPLATES
                  </button>
                </div>
              </div>
              {[
                [agentWho,setAgentWho,"WHO","e.g. Dr. Smith's dental office"],
                [agentGoal,setAgentGoal,"GOAL","e.g. Reschedule Thursday 2pm appointment to Friday"],
                [agentContext,setAgentContext,"CONTEXT","e.g. My name is Ethan Kramer, DOB 01/15/1985"],
              ].map(([val,set,label,ph],i)=>(
                <div key={i} style={{display:"flex",flexDirection:"column",gap:3}}>
                  <div style={{fontSize:10,fontFamily:"var(--mono)",fontWeight:700,color:"var(--ac2)",letterSpacing:"0.08em"}}>{label}</div>
                  <input value={val} onChange={e=>set(e.target.value)} placeholder={ph}
                    style={{width:"100%",fontSize:13,color:"var(--tx)",caretColor:"var(--ac)",background:"var(--s2)",border:"1px solid var(--s3)",borderRadius:8,padding:"8px 10px"}}/>
                </div>
              ))}
              <div style={{display:"flex",flexDirection:"column",gap:3}}>
                <div style={{fontSize:10,fontFamily:"var(--mono)",fontWeight:700,color:"var(--ac2)",letterSpacing:"0.08em"}}>INSTRUCTIONS</div>
                <textarea value={agentInstructions} onChange={e=>setAgentInstructions(e.target.value)}
                  placeholder="Fallbacks & preferences (e.g. If unavailable, any Mon-Fri 9-5 works. No voicemail.)"
                  rows={2}
                  style={{width:"100%",fontSize:13,color:"var(--tx)",caretColor:"var(--ac)",background:"var(--s2)",border:"1px solid var(--s3)",borderRadius:8,padding:"8px 10px",resize:"none",fontFamily:"inherit"}}/>
              </div>
              <div style={{fontSize:9,color:"var(--ac2)",fontFamily:"var(--mono)",opacity:0.6}}>Claude handles the call. Tap "Take Over" anytime.</div>
            </div>
          )}

          {/* Translator language picker */}
          {/* Translator panel removed — translator is now a capability toggle
              (translatorEnabled) rather than a third mode. The pill above the
              dial pad lets users turn it on/off and pick a language; the
              language selection modal is rendered at the top level (next to
              showTemplatePicker). */}

        </div>
        )} {/* end tab conditional */}
        {/* Call button - fixed at bottom */}
        {tab === "dial" && (
          <div style={{position:"absolute",bottom:0,left:0,right:0,padding:"10px 16px 12px",paddingBottom:"max(12px,env(safe-area-inset-bottom))",background:"var(--bg)",borderTop:"1px solid var(--s3)"}}>
            {/* Translator capability pill — toggleable, available in both Manual and AI Agent modes */}
            <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:8}}>
              <button
                onClick={()=>saveTranslatorEnabled(!translatorEnabled)}
                style={{
                  display:"flex",alignItems:"center",gap:6,
                  padding:"6px 11px",borderRadius:999,
                  background:translatorEnabled?"rgba(139,92,246,0.15)":"var(--s2)",
                  border:`1px solid ${translatorEnabled?"#8b5cf6":"var(--s3)"}`,
                  fontSize:11,fontWeight:700,
                  color:translatorEnabled?"#a78bfa":"var(--tx3)",
                  fontFamily:"var(--mono)",letterSpacing:"0.04em",
                  cursor:"pointer",transition:"all .15s",
                }}
                title={translatorEnabled?"Translator on — tap to turn off":"Turn translator on"}
              >
                <span style={{fontSize:13,lineHeight:1}}>🌐</span>
                TRANSLATOR
                <span style={{
                  width:18,height:11,borderRadius:6,
                  background:translatorEnabled?"#8b5cf6":"var(--s3)",
                  position:"relative",transition:"all .2s",marginLeft:2,
                }}>
                  <span style={{
                    position:"absolute",top:1,left:translatorEnabled?8:1,
                    width:9,height:9,borderRadius:"50%",background:"#fff",
                    transition:"left .2s",
                  }}/>
                </span>
              </button>
              {translatorEnabled && (
                <button
                  onClick={()=>setShowLanguagePicker(true)}
                  style={{
                    display:"flex",alignItems:"center",gap:5,
                    padding:"6px 10px",borderRadius:999,
                    background:"rgba(139,92,246,0.08)",
                    border:"1px solid rgba(139,92,246,0.4)",
                    fontSize:11,fontWeight:600,
                    color:"#a78bfa",cursor:"pointer",
                  }}
                  title="Change language"
                >
                  <span style={{fontSize:14,lineHeight:1}}>{targetLanguage.flag}</span>
                  {targetLanguage.name}
                  <span style={{fontSize:9,opacity:0.6}}>▾</span>
                </button>
              )}
            </div>
            <button onClick={startCall} disabled={!canCall} className={canCall?"call-btn-active":""}
              style={{width:"100%",padding:"14px",borderRadius:14,background:canCall?"var(--ac)":"var(--s2)",color:canCall?"#fff":"var(--tx3)",fontSize:16,fontWeight:700,letterSpacing:"-0.01em",transition:"all .2s",border:"none",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
              <IconPhone size={16} color={canCall?"#fff":"var(--tx3)"}/>
              {canCall?"Call":"Enter a number"}
            </button>
          </div>
        )}
      </div>
    </>
  );
}
// build enhanced-ui
